import {
  ExpandMore,
  FileDownload,
  FilterList,
  Image as ImageIcon,
  Insights,
  KeyboardArrowDown,
  KeyboardArrowUp,
  Person as PersonIcon,
  Search,
  PlaylistAddCheck,
} from "@mui/icons-material";
import {
  Avatar,
  Badge,
  Box,
  Button,
  CircularProgress,
  Collapse,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  FormControl,
  IconButton,
  InputBase,
  List,
  ListItemAvatar,
  ListItemButton,
  MenuItem,
  Select,
  styled,
  Tooltip,
  Typography,
  useMediaQuery,
  useTheme,
  Checkbox,
} from "@mui/material";
import type { AxiosResponse } from "axios";
import React, {
  memo,
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
} from "react";
import {
  Cell,
  Legend,
  Pie,
  PieChart,
  Tooltip as RechartsTooltip,
  ResponsiveContainer,
} from "recharts";
import { useAccount, useChat, useSidebar, useToast } from "../../../context";
import { useConversationReadStatus } from "../../../hooks";
import { useApiError } from "../../../hooks/useApiError";
import { useMultipleCampaigns } from "../../../hooks/useConversationsManager";
import { useChatStore } from "../../../store/chatStore";
import { markAllMessagesAsRead, toggleAIState } from "../../../services/api";
import {
  ConversationData,
  ConversationMetrics,
  FilterBadgeProps,
  FilterType,
  GroupState,
  MessageType,
} from "../../../interfaces/all";
import { apiService } from "../../../services/axios";
import { formatMessageTime } from "../../../utils/date";
import { normalizePhoneNumber } from "../../../utils/phone-utils";
import { ConversationSkeleton } from "../../shared/SkeletonLoader";
import Header from "../Header/Header";

const Container = styled(Box)`
  width: 100%;
  height: 100%;
  background: #fff;
  border-right: 1px solid #e9edef;
  display: flex;
  flex-direction: column;
  box-sizing: border-box;
  position: relative;

  @media (max-width: 600px) {
    width: 100%;
    min-width: 100%;
    max-width: 100%;
  }
`;

const ContentContainer = styled(Box)`
  flex: 1;
  overflow: hidden; /* Padronizado com TemplatesSidebar: rolagem fica no conteúdo interno */
  display: flex;
  flex-direction: column;
  max-height: calc(100vh - 60px); /* Limitar altura para forçar scroll */
`;

const SearchContainer = styled(Box)`
  padding: 6px;
  background: #f0f2f5;
  border-bottom: 1px solid #e9edef;
  display: flex;
  flex-direction: column;
  gap: 8px;
`;

const SearchRow = styled(Box)`
  display: flex;
  align-items: center;
  gap: 6px;
`;

const CampaignFilterContainer = styled(Collapse)`
  width: 100%;
  margin-top: 4px;
`;

const SearchInputWrapper = styled(Box, {
  shouldForwardProp: (prop) =>
    prop !== "hasActiveSearch" && prop !== "hasUnsearchedText",
})<{ hasActiveSearch?: boolean; hasUnsearchedText?: boolean }>`
  flex: 1;
  background: ${(props) => {
    if (props.hasActiveSearch) return "#f0f8ff";
    if (props.hasUnsearchedText) return "#fff9e6";
    return "#fff";
  }};
  border-radius: 8px;
  padding: 4px 8px;
  display: flex;
  align-items: center;
  gap: 6px;
  position: relative;
  border: 1px solid
    ${(props) => {
      if (props.hasActiveSearch) return "#008069";
      if (props.hasUnsearchedText) return "#ffc107";
      return "transparent";
    }};
  transition: all 0.2s ease;
`;

const SearchInput = styled(InputBase)`
  flex: 1;
  font-size: 14px;
  color: #111b21;
  &::placeholder {
    color: #667781;
  }
`;

const ConversationList = styled(List)`
  flex: 1;
  overflow-y: auto; /* Área rolável fica aqui, como em TemplatesSidebar */
  padding: 0;
  max-height: calc(100vh - 200px); /* Limitar altura para forçar scroll */
`;

const ConversationItem = styled(ListItemButton, {
  shouldForwardProp: (prop) =>
    prop !== "selected" &&
    prop !== "isSearchMatch" &&
    prop !== "isCurrentMatch",
})<{ selected?: boolean; isSearchMatch?: boolean; isCurrentMatch?: boolean }>`
  padding: 12px 16px;
  background: ${(props) => {
    if (props.isCurrentMatch) return "#d4edda";
    if (props.selected) return "#e9edef";
    if (props.isSearchMatch) return "#f0f8ff";
    return "transparent";
  }};
  border-bottom: 1px solid #f0f2f5;
  border-left: ${(props) => {
    if (props.isCurrentMatch) return "3px solid #28a745";
    if (props.isSearchMatch) return "3px solid #008069";
    return "none";
  }};
  position: relative;
  overflow: visible;
  height: 72px;
  min-height: 72px;
  max-height: 72px;
  display: flex;
  align-items: center;

  @media (max-width: 600px) {
    padding: 6px 8px;
    height: 56px;
    min-height: 56px;
    max-height: 56px;
    overflow: visible;
  }

  &::before {
    content: "";
    position: absolute;
    left: 0;
    top: 0;
    bottom: 0;
    width: 3px;
    background-color: ${(props) => {
      if (props.isCurrentMatch) return "#28a745";
      if (props.selected) return "#00a884";
      return "transparent";
    }};
    z-index: 1;
  }

  &:hover {
    background: ${(props) => {
      if (props.isCurrentMatch) return "#c3e6cb";
      if (props.selected) return "#e9edef";
      if (props.isSearchMatch) return "#e6f3ff";
      return "#f5f6f6";
    }};
  }

  ${(props) =>
    props.selected &&
    `
        &:hover {
            background: #e9edef;
        }
    `}
`;

const ConversationInfo = styled(Box)`
  flex: 1;
  min-width: 0;
  margin-left: 12px;
  overflow: visible;

  @media (max-width: 600px) {
    margin-left: 4px;
    overflow: visible;
  }
`;

const ConversationName = styled(Typography)`
  font-size: 16px;
  font-weight: 500;
  color: #111b21;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
  flex: 1;
  min-width: 0;
  max-width: calc(100% - 60px);

  @media (max-width: 600px) {
    font-size: 12px;
    max-width: calc(100% - 60px);
  }
`;

const ConversationTime = styled(Typography)`
  font-size: 12px;
  color: #667781;
  white-space: nowrap;
  margin-left: 6px;
  flex-shrink: 0;
  min-width: 60px;
  text-align: right;

  @media (max-width: 600px) {
    font-size: 10px;
    margin-left: 2px;
    min-width: 50px;
  }
`;

const ConversationMessage = styled(Typography)`
  font-size: 14px;
  color: #667781;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;

  @media (max-width: 600px) {
    font-size: 11px;
  }
`;

// ConversationGroup component
const ConversationGroup = styled(Box)({
  "& .group-header": {
    padding: "10px 16px",
    backgroundColor: "#f0f2f5",
    display: "flex",
    alignItems: "center",
    justifyContent: "space-between",
    cursor: "pointer",
    gap: "8px",
    transition: "all 0.2s ease",
    position: "relative",
    "&:hover": {
      backgroundColor: "#e9edef",
      "& .group-title": {
        color: "#111b21",
      },
      "& .group-count": {
        backgroundColor: "#d1d7db",
      },
    },
    "&:active": {
      backgroundColor: "#e2e7e9",
    },
  },
  "& .group-title": {
    fontSize: "0.9rem",
    fontWeight: 500,
    color: "#54656f",
    flex: 1,
    minWidth: 0,
    overflow: "hidden",
    textOverflow: "ellipsis",
    whiteSpace: "nowrap",
    transition: "color 0.2s ease",
  },
  "& .group-count": {
    fontSize: "0.75rem",
    color: "#54656f",
    backgroundColor: "#e9edef",
    padding: "2px 8px",
    borderRadius: "12px",
    marginRight: "4px",
    fontWeight: 500,
    transition: "all 0.2s ease",
    border: "1px solid transparent",
    minWidth: "fit-content",
    textAlign: "center",
    "&:hover": {
      backgroundColor: "#d1d7db",
      borderColor: "#bdbdbd",
    },
  },
  // Removida rolagem interna do grupo para evitar barras de rolagem aninhadas
  "& .MuiCollapse-root": {
    maxHeight: "min(50vh, calc(100vh - 220px))", // Limite responsivo para não competir com a rolagem externa
    overflowY: "auto",
    overscrollBehavior: "contain", // Evita scroll chaining que confunde o usuário
    "& .MuiList-root": {
      padding: "4px 0",
      backgroundColor: "#fff",
    },
  },
  // Realce de delimitação quando a campanha estiver expandida
  "& .MuiCollapse-root.MuiCollapse-entered": {
    borderBottom: "3px solid #e9edef",
    marginBottom: "8px",
  },
  "& .MuiList-root": {
    padding: 0,
  },
  "& .expand-icon": {
    transition: "transform 0.2s ease",
    color: "#54656f",
    opacity: 0.8,
    "&.expanded": {
      transform: "rotate(180deg)",
      color: "#008069",
    },
  },
});

const StyledBadge = styled(Badge)(({ theme }) => ({
  "& .MuiBadge-badge": {
    backgroundColor: "#44b700",
    color: "#44b700",
    boxShadow: `0 0 0 2px ${theme.palette.background.paper}`,
    "&::after": {
      position: "absolute",
      top: 0,
      left: 0,
      width: "100%",
      height: "100%",
      borderRadius: "50%",
      animation: "ripple 1.2s infinite ease-in-out",
      border: "1px solid currentColor",
      content: '""',
    },
  },
  "@keyframes ripple": {
    "0%": {
      transform: "scale(.8)",
      opacity: 1,
    },
    "100%": {
      transform: "scale(2.4)",
      opacity: 0,
    },
  },
}));

const FilterContainer = styled(Box)`
  padding: 4px 6px;
  background: #f0f2f5;
  border-bottom: 1px solid #e9edef;
  display: flex;
  align-items: center;
  gap: 4px;
  overflow-x: auto;
  &::-webkit-scrollbar {
    height: 4px;
  }
  &::-webkit-scrollbar-thumb {
    background-color: #bdbdbd;
    border-radius: 4px;
  }
`;

const FilterBadgeBase: React.FC<FilterBadgeProps> = ({
  isActive,
  onClick,
  children,
}) => (
  <Box
    onClick={onClick}
    sx={{
      display: "flex",
      alignItems: "center",
      padding: "4px 12px",
      borderRadius: "16px",
      background: isActive ? "#e9edef" : "#fff",
      color: isActive ? "#111b21" : "#54656f",
      fontSize: "13px",
      fontWeight: isActive ? 500 : 400,
      cursor: "pointer",
      whiteSpace: "nowrap",
      border: `1px solid ${isActive ? "#bdbdbd" : "#e9edef"}`,
      transition: "all 0.2s ease",
      "&:hover": {
        background: isActive ? "#e9edef" : "#f5f6f6",
      },
    }}
  >
    {children}
  </Box>
);

const UnreadBadge = styled(Badge)(() => ({
  "& .MuiBadge-badge": {
    backgroundColor: "#00a884",
    color: "#fff",
    minWidth: "20px",
    height: "20px",
    borderRadius: "10px",
    fontSize: "12px",
    fontWeight: 500,
    padding: "0 6px",
  },
}));

const ConversationAvatar = styled(ListItemAvatar)`
  min-width: 49px;
  margin-right: 12px;

  @media (max-width: 600px) {
    min-width: 28px;
    margin-right: 4px;
  }
`;

const LoadingMoreContainer = styled(Box)`
  display: flex;
  justify-content: center;
  align-items: center;
  padding: 16px;
  background: #f8f9fa;
`;

const FloatingActionBar = styled(Box)`
  position: absolute;
  bottom: 80px;
  left: 50%;
  transform: translateX(-50%);
  background: rgba(255, 255, 255, 0.9);
  backdrop-filter: blur(12px);
  border: 1px solid rgba(0, 168, 132, 0.2);
  box-shadow: 0 8px 32px 0 rgba(11, 20, 26, 0.16);
  border-radius: 12px;
  padding: 12px 18px;
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 12px;
  z-index: 100;
  width: 92%;
  box-sizing: border-box;
  animation: slideUp 0.35s cubic-bezier(0.22, 1, 0.36, 1) forwards;

  @keyframes slideUp {
    from {
      opacity: 0;
      transform: translate(-50%, 24px);
    }
    to {
      opacity: 1;
      transform: translate(-50%, 0);
    }
  }
`;

// Função helper para destacar texto pesquisado (destaca todas as ocorrências)
// Usa busca case-insensitive mas preserva o texto original no destaque
const highlightText = (text: string, searchTerm: string): React.ReactNode => {
  if (!searchTerm || !text) {
    return text;
  }

  const searchLower = searchTerm.toLowerCase().trim();
  if (!searchLower) {
    return text;
  }

  const textLower = text.toLowerCase();

  // Verificar se o texto realmente contém a busca completa
  if (!textLower.includes(searchLower)) {
    return text;
  }

  const parts: React.ReactNode[] = [];
  let lastIndex = 0;
  let index = textLower.indexOf(searchLower, lastIndex);

  while (index !== -1) {
    // Adicionar texto antes da correspondência
    if (index > lastIndex) {
      parts.push(text.substring(lastIndex, index));
    }

    // Adicionar correspondência destacada (usar o texto original, não o lowercase)
    // IMPORTANTE: Usar searchLower.length para garantir que destacamos o tamanho correto
    const matchLength = searchLower.length;
    const highlightedText = text.substring(index, index + matchLength);

    parts.push(
      <span
        key={`highlight-${index}`}
        style={{
          backgroundColor: "#fff3cd",
          color: "#856404",
          fontWeight: 600,
          padding: "1px 2px",
          borderRadius: "3px",
        }}
      >
        {highlightedText}
      </span>,
    );

    lastIndex = index + matchLength;
    index = textLower.indexOf(searchLower, lastIndex);
  }

  // Adicionar texto restante após a última correspondência
  if (lastIndex < text.length) {
    parts.push(text.substring(lastIndex));
  }

  return parts.length > 0 ? <>{parts}</> : text;
};

// Componente memoizado para conversa individual com comparação otimizada
const ConversationItemMemo = memo(
  ({
    conversation,
    index,
    isSelected,
    onConversationClick,
    getConversationKey,
    getUnreadCount,
    searchText = "",
    isCurrentMatch = false,
    itemRef,
    isBulkMode = false,
    isSelectedBulk = false,
    onToggleBulk,
  }: {
    conversation: ConversationData;
    index: number;
    isSelected: boolean;
    onConversationClick: (conversation: ConversationData) => void;
    getConversationKey: (
      conversation: ConversationData,
      index: number,
    ) => string;
    getUnreadCount: (conversation: ConversationData) => number;
    searchText?: string;
    isCurrentMatch?: boolean;
    itemRef?: React.RefObject<HTMLDivElement>;
    isBulkMode?: boolean;
    isSelectedBulk?: boolean;
    onToggleBulk?: (phone: string) => void;
  }) => {
    const unreadCount = getUnreadCount(conversation);
    const typingState = useChatStore((state) => state.typingState);
    const activePhone =
      conversation.valid_telephone || conversation.telephone || "";
    const isTyping = activePhone ? !!typingState[activePhone] : false;

    // Verificar se esta conversa corresponde à pesquisa
    const isSearchMatch = searchText.trim()
      ? (() => {
          const searchLower = searchText.toLowerCase().trim();
          const name = (
            conversation.valid_name ||
            conversation.name ||
            ""
          ).toLowerCase();
          const campaign = (conversation.campaign_name || "").toLowerCase();
          const phone = (
            conversation.valid_telephone ||
            conversation.telephone ||
            ""
          ).replace(/\D/g, "");
          const searchPhone = searchLower.replace(/\D/g, "");

          return (
            name.includes(searchLower) ||
            campaign.includes(searchLower) ||
            phone.includes(searchPhone)
          );
        })()
      : false;

    // Obter o nome original antes da formatação para usar na verificação de correspondência
    const originalName =
      conversation.valid_name || conversation.name || "Desconhecido";
    const displayName = originalName
      .split(" ")
      .map((word) => word.charAt(0).toUpperCase() + word.slice(1).toLowerCase())
      .join(" ");

    return (
      <ConversationItem
        ref={itemRef}
        key={getConversationKey(conversation, index)}
        selected={isSelected}
        isSearchMatch={isSearchMatch}
        isCurrentMatch={isCurrentMatch}
        onClick={() =>
          isBulkMode
            ? onToggleBulk?.(activePhone)
            : onConversationClick(conversation)
        }
      >
        {isBulkMode && (
          <Checkbox
            checked={isSelectedBulk}
            onChange={(e) => {
              e.stopPropagation();
              onToggleBulk?.(activePhone);
            }}
            sx={{
              color: "#00a884",
              "&.Mui-checked": {
                color: "#00a884",
              },
              mr: 1,
              p: 0.5,
            }}
          />
        )}
        <ConversationAvatar>
          <StyledBadge
            overlap="circular"
            anchorOrigin={{ vertical: "bottom", horizontal: "right" }}
            invisible={!conversation.is_online}
          >
            <Avatar sx={{ width: 50, height: 50, bgcolor: "#bdbdbd" }}>
              <PersonIcon sx={{ width: 40, height: 40 }} />
            </Avatar>
          </StyledBadge>
        </ConversationAvatar>
        <ConversationInfo
          sx={{
            display: "flex",
            flexDirection: "column",
            justifyContent: "center",
            width: "100%",
            height: "100%",
          }}
        >
          <Box
            sx={{
              display: "flex",
              flexDirection: "row",
              alignItems: "center",
              justifyContent: "space-between",
              width: "100%",
            }}
          >
            <ConversationName>
              {(() => {
                // Só destacar se houver pesquisa E se o nome realmente contém a busca completa
                const trimmedSearch = searchText.trim();
                if (!trimmedSearch || !isSearchMatch) {
                  return displayName;
                }

                // Garantir que estamos usando o searchText completo (não truncado)
                const searchLower = trimmedSearch.toLowerCase();
                // Usar o nome original (não formatado) para verificar correspondência
                const nameLower = originalName.toLowerCase();

                // Verificar se o nome realmente contém a busca completa antes de destacar
                if (!nameLower.includes(searchLower)) {
                  return displayName;
                }

                // Usar o displayName formatado para destacar, mas garantir que o searchText completo seja usado
                return highlightText(displayName, trimmedSearch);
              })()}
            </ConversationName>
            <ConversationTime>
              {formatMessageTime(conversation.last_message?.timestamp)}
            </ConversationTime>
          </Box>
          <Box
            sx={{
              display: "flex",
              flexDirection: "row",
              alignItems: "center",
              justifyContent: "space-between",
              width: "100%",
            }}
          >
            <ConversationMessage
              sx={{
                flex: 1,
                minWidth: 0,
                overflow: "hidden",
                textOverflow: "ellipsis",
                whiteSpace: "nowrap",
                fontWeight:
                  conversation.last_message?.sentBy === "client" ? 600 : 400,
                color:
                  conversation.last_message?.sentBy === "client"
                    ? "#111b21"
                    : "#667781",
              }}
            >
              {(() => {
                if (isTyping) {
                  return (
                    <span style={{ color: "#00a884", fontWeight: 600 }}>
                      digitando...
                    </span>
                  );
                }
                const msg = conversation.last_message;
                if (!msg || !msg.type) return "Nenhuma mensagem";
                switch (msg.type) {
                  case "text":
                    return msg.content?.text || "Texto";
                  case "image":
                    return (
                      <span
                        style={{
                          display: "inline-flex",
                          alignItems: "center",
                          gap: 4,
                        }}
                      >
                        <ImageIcon
                          style={{
                            fontSize: 16,
                            color: "#bdbdbd",
                            marginRight: 4,
                          }}
                        />
                        Imagem
                      </span>
                    );
                  case "template":
                    return (
                      <span
                        style={{
                          display: "inline-flex",
                          alignItems: "center",
                          gap: 4,
                        }}
                      >
                        <span style={{ fontSize: 16, marginRight: 4 }}>📄</span>
                        Template:{" "}
                        {msg.content?.templateData?.templateName || "Template"}
                      </span>
                    );
                  case "button":
                    return (
                      <span
                        style={{
                          display: "inline-flex",
                          alignItems: "center",
                          gap: 4,
                        }}
                      >
                        <span style={{ fontSize: 16, marginRight: 4 }}>🔘</span>
                        Botão clicado
                      </span>
                    );
                  case "reaction":
                    return (
                      <span
                        style={{
                          display: "inline-flex",
                          alignItems: "center",
                          gap: 4,
                        }}
                      >
                        <span style={{ fontSize: 16, marginRight: 4 }}>👍</span>
                        Reação
                      </span>
                    );
                  case "unsupported":
                    return (
                      <span
                        style={{
                          display: "inline-flex",
                          alignItems: "center",
                          gap: 4,
                        }}
                      >
                        <span style={{ fontSize: 16, marginRight: 4 }}>⚠️</span>
                        Mensagem não suportada
                      </span>
                    );
                  default:
                    return `Mensagem não suportada (${msg.type})`;
                }
              })()}
            </ConversationMessage>
            {unreadCount > 0 && (
              <UnreadBadge
                badgeContent={unreadCount}
                color="primary"
                sx={{
                  mr: 1,
                  "& .MuiBadge-badge": {
                    backgroundColor: "#00a884",
                    color: "#fff",
                    minWidth: "20px",
                    height: "20px",
                    borderRadius: "10px",
                    fontSize: "12px",
                    fontWeight: 500,
                    padding: "0 6px",
                    boxShadow: "0 1px 2px rgba(0,0,0,0.08)",
                  },
                }}
              />
            )}
          </Box>
        </ConversationInfo>
      </ConversationItem>
    );
  },
  (prevProps, nextProps) => {
    // Comparação customizada otimizada para evitar re-renders desnecessários
    // Só re-renderizar se houver mudanças reais nos dados

    // IMPORTANTE: Verificar mudanças no searchText para atualizar highlights
    const searchTextChanged =
      (prevProps.searchText || "").trim() !==
      (nextProps.searchText || "").trim();
    if (searchTextChanged) {
      return false; // Permitir re-render quando searchText muda
    }

    // Verificar mudanças no isCurrentMatch para atualizar destaque de navegação
    if (prevProps.isCurrentMatch !== nextProps.isCurrentMatch) {
      return false; // Permitir re-render quando isCurrentMatch muda
    }

    const prevConversation = prevProps.conversation;
    const nextConversation = nextProps.conversation;

    // Verificar se é a mesma conversa
    if (prevConversation.id !== nextConversation.id) {
      return false; // Permitir re-render
    }

    // Verificar mudanças na última mensagem (só se realmente mudou)
    const prevLastMessage = prevConversation.last_message;
    const nextLastMessage = nextConversation.last_message;

    const lastMessageChanged =
      prevLastMessage?._id !== nextLastMessage?._id ||
      prevLastMessage?.timestamp !== nextLastMessage?.timestamp ||
      prevLastMessage?.content?.text !== nextLastMessage?.content?.text ||
      prevLastMessage?.type !== nextLastMessage?.type ||
      prevLastMessage?.sentBy !== nextLastMessage?.sentBy ||
      // Adicionar comparação de status para detectar mudanças de status
      prevLastMessage?.status !== nextLastMessage?.status ||
      // Comparar timestamps como números para detectar mudanças de data
      (prevLastMessage?.timestamp &&
        nextLastMessage?.timestamp &&
        new Date(prevLastMessage.timestamp).getTime() !==
          new Date(nextLastMessage.timestamp).getTime());

    // Verificar mudanças na seleção
    const selectionChanged = prevProps.isSelected !== nextProps.isSelected;

    // Verificar se mudou o estado de modo em lote ou seleção em lote
    const bulkStateChanged =
      prevProps.isBulkMode !== nextProps.isBulkMode ||
      prevProps.isSelectedBulk !== nextProps.isSelectedBulk;
    if (bulkStateChanged) {
      return false;
    }

    // IMPORTANTE: Verificar mudanças no contador de mensagens não lidas
    const prevUnreadCount = prevProps.getUnreadCount(prevConversation);
    const nextUnreadCount = nextProps.getUnreadCount(nextConversation);
    const unreadCountChanged = prevUnreadCount !== nextUnreadCount;

    // Só re-renderizar se houver mudanças críticas
    // Remover mudanças que causam flicker desnecessário
    const shouldUpdate =
      lastMessageChanged || selectionChanged || unreadCountChanged;

    return !shouldUpdate; // Retornar true para evitar re-render, false para permitir
  },
);

ConversationItemMemo.displayName = "ConversationItemMemo";

// Componente para campanha individual com paginação
const CampaignGroup = memo(
  ({
    campaign,
    conversations,
    expandedGroups,
    selectedConversation,
    onConversationClick,
    getConversationKey,
    getUnreadCount,
    hasMore,
    loading,
    onLoadMore,
    total,
    isLoaded,
    onToggleGroup,
    searchText = "",
    currentSearchIndex = -1,
    searchMatches = [],
    searchMatchesRefs,
    matchingCampaigns = [],
    onMetricsClick,
    showMetrics,
    isBulkMode = false,
    selectedBulkChats = new Set(),
    onToggleBulk,
  }: {
    campaign: string;
    conversations: ConversationData[];
    expandedGroups: GroupState;
    selectedConversation: ConversationData | null;
    onConversationClick: (conversation: ConversationData) => void;
    getConversationKey: (
      conversation: ConversationData,
      index: number,
    ) => string;
    getUnreadCount: (conversation: ConversationData) => number;
    hasMore: boolean;
    loading: boolean;
    onLoadMore: () => void;
    total?: number;
    isLoaded: boolean;
    onToggleGroup: (campaignName: string) => void;
    searchText?: string;
    currentSearchIndex?: number;
    searchMatches?: ConversationData[];
    searchMatchesRefs?: React.MutableRefObject<
      Map<string, React.RefObject<HTMLDivElement>>
    >;
    matchingCampaigns?: string[];
    showMetrics?: boolean;
    onMetricsClick?: (campaignName: string) => void;
    isBulkMode?: boolean;
    selectedBulkChats?: Set<string>;
    onToggleBulk?: (phone: string) => void;
  }) => {
    const listRef = useRef<HTMLUListElement>(null);
    const resetTimeoutRef = useRef<ReturnType<typeof setTimeout> | null>(null);

    const hasClientReplies = useMemo(() => {
      return conversations.some(
        (conv) => conv.last_message?.sentBy === "client",
      );
    }, [conversations]);

    // Virtualization scroll state variables
    const [scrollTop, setScrollTop] = useState(0);
    const [viewportHeight, setViewportHeight] = useState(600);
    const [listOffsetTop, setListOffsetTop] = useState(0);

    // Scroll parent logic
    const getScrollParent = useCallback(
      (node: HTMLElement | null): HTMLElement | null => {
        if (!node) return null;
        let parent = node.parentElement;
        while (parent) {
          const overflowY = window.getComputedStyle(parent).overflowY;
          if (overflowY === "auto" || overflowY === "scroll") {
            return parent;
          }
          parent = parent.parentElement;
        }
        return document.documentElement;
      },
      [],
    );

    useEffect(() => {
      if (!expandedGroups[campaign]) return;

      const scrollParent = getScrollParent(listRef.current);
      if (!scrollParent) return;

      let animationFrameId: number;

      const handleScroll = () => {
        cancelAnimationFrame(animationFrameId);
        animationFrameId = requestAnimationFrame(() => {
          if (scrollParent) {
            setScrollTop(scrollParent.scrollTop);
            setViewportHeight(scrollParent.clientHeight);
          }

          if (listRef.current && scrollParent) {
            const rect = listRef.current.getBoundingClientRect();
            const parentRect = scrollParent.getBoundingClientRect();
            const offset = rect.top - parentRect.top + scrollParent.scrollTop;
            setListOffsetTop(offset);
          }
        });
      };

      handleScroll();

      scrollParent.addEventListener("scroll", handleScroll, { passive: true });
      window.addEventListener("resize", handleScroll);

      const timeoutId = setTimeout(handleScroll, 350);

      return () => {
        cancelAnimationFrame(animationFrameId);
        clearTimeout(timeoutId);
        scrollParent.removeEventListener("scroll", handleScroll);
        window.addEventListener("resize", handleScroll);
      };
    }, [expandedGroups, campaign, getScrollParent]);

    // Observer para detectar quando chegar ao final da lista
    useEffect(() => {
      if (!expandedGroups[campaign] || !hasMore || loading) return;

      let isObserving = false;
      const observer = new IntersectionObserver(
        (entries) => {
          entries.forEach((entry) => {
            if (entry.isIntersecting && hasMore && !loading && !isObserving) {
              isObserving = true;

              onLoadMore();
              if (resetTimeoutRef.current) {
                clearTimeout(resetTimeoutRef.current);
              }

              resetTimeoutRef.current = setTimeout(() => {
                isObserving = false;
              }, 2000);
            }
          });
        },
        { threshold: 0.1, rootMargin: "100px" },
      );

      const sentinel = document.createElement("div");
      sentinel.style.height = "1px";
      sentinel.style.width = "100%";

      if (listRef.current) {
        listRef.current.appendChild(sentinel);
        observer.observe(sentinel);
      }

      return () => {
        observer.disconnect();
        if (sentinel.parentNode) {
          sentinel.parentNode.removeChild(sentinel);
        }
        if (resetTimeoutRef.current) {
          clearTimeout(resetTimeoutRef.current);
        }
      };
    }, [
      expandedGroups[campaign],
      hasMore,
      loading,
      campaign,
      onLoadMore,
      expandedGroups,
    ]);

    // Verificar se esta campanha está destacada na navegação atual
    const isCampaignHighlighted = useMemo(() => {
      if (!searchText.trim() || currentSearchIndex < 0) {
        return false;
      }

      if (
        currentSearchIndex >= searchMatches.length &&
        matchingCampaigns.length > 0
      ) {
        const campaignIndex = currentSearchIndex - searchMatches.length;
        if (
          campaignIndex < matchingCampaigns.length &&
          matchingCampaigns[campaignIndex] === campaign
        ) {
          return true;
        }
      }

      return false;
    }, [
      searchText,
      currentSearchIndex,
      campaign,
      searchMatches,
      matchingCampaigns,
    ]);

    // Virtual list calculations
    const ITEM_HEIGHT = window.matchMedia("(max-width: 600px)").matches
      ? 56
      : 72;
    const relativeScrollTop = Math.max(0, scrollTop - listOffsetTop);
    const startIndex = Math.max(
      0,
      Math.floor(relativeScrollTop / ITEM_HEIGHT) - 5,
    );
    const endIndex = Math.min(
      conversations.length,
      Math.ceil((relativeScrollTop + viewportHeight) / ITEM_HEIGHT) + 5,
    );

    const visibleConversations = useMemo(() => {
      return conversations.slice(startIndex, endIndex).map((conv, idx) => ({
        conversation: conv,
        originalIndex: startIndex + idx,
      }));
    }, [conversations, startIndex, endIndex]);

    const topSpacerHeight = startIndex * ITEM_HEIGHT;
    const bottomSpacerHeight = (conversations.length - endIndex) * ITEM_HEIGHT;

    return (
      <ConversationGroup key={campaign}>
        <Box
          className="group-header"
          data-campaign-name={campaign}
          onClick={() => onToggleGroup(campaign)}
          sx={{
            backgroundColor:
              (searchText.trim() &&
                campaign
                  .toLowerCase()
                  .includes(searchText.toLowerCase().trim())) ||
              isCampaignHighlighted
                ? isCampaignHighlighted
                  ? "rgba(0, 128, 105, 0.2)"
                  : "rgba(0, 128, 105, 0.1)"
                : "transparent",
            border: isCampaignHighlighted ? "2px solid #008069" : "none",
            transition: "background-color 0.2s ease, border 0.2s ease",
          }}
        >
          <Box
            sx={{
              display: "flex",
              alignItems: "center",
              gap: 1,
              minWidth: 0,
              flex: 1,
            }}
          >
            {showMetrics && (
              <IconButton
                size="small"
                onClick={(event) => {
                  event.stopPropagation();
                  onMetricsClick?.(campaign);
                }}
                sx={{
                  padding: "8px",
                  color: "#008069",
                  "&:hover": { backgroundColor: "rgba(0, 128, 105, 0.1)" },
                }}
                title="Ver métricas da campanha"
              >
                <Insights sx={{ fontSize: 15 }} />
              </IconButton>
            )}
            <Tooltip title={campaign} arrow placement="top">
              <Typography
                className="group-title"
                sx={{
                  fontWeight: hasClientReplies
                    ? "700 !important"
                    : "500 !important",
                  color: hasClientReplies
                    ? "#111b21 !important"
                    : "#54656f !important",
                }}
              >
                {searchText.trim() &&
                campaign.toLowerCase().includes(searchText.toLowerCase().trim())
                  ? highlightText(campaign, searchText)
                  : campaign}
              </Typography>
            </Tooltip>
          </Box>
          <Typography className="group-count">
            {isLoaded ? conversations.length : "-"} /{" "}
            {isLoaded ? (total ?? 0) : "-"}
          </Typography>
          <ExpandMore
            className={`expand-icon ${expandedGroups[campaign] ? "expanded" : ""}`}
          />
        </Box>
        <Collapse in={expandedGroups[campaign]}>
          <List ref={listRef}>
            {topSpacerHeight > 0 && (
              <Box sx={{ height: `${topSpacerHeight}px` }} />
            )}
            {visibleConversations.map(({ conversation, originalIndex }) => {
              const key = getConversationKey(conversation, originalIndex);
              if (!searchMatchesRefs?.current.has(key)) {
                searchMatchesRefs?.current.set(
                  key,
                  React.createRef<HTMLDivElement>() as React.RefObject<HTMLDivElement>,
                );
              }
              const itemRef = searchMatchesRefs?.current.get(key);

              const globalIndex =
                searchMatches?.findIndex(
                  (match) =>
                    match.id === conversation.id &&
                    match.campaign_name === conversation.campaign_name,
                ) ?? -1;
              const isCurrentMatch =
                globalIndex >= 0 && globalIndex === currentSearchIndex;

              const activePhone =
                conversation.valid_telephone || conversation.telephone || "";

              return (
                <ConversationItemMemo
                  key={key}
                  conversation={conversation}
                  index={originalIndex}
                  isSelected={selectedConversation?.id === conversation.id}
                  onConversationClick={onConversationClick}
                  getConversationKey={getConversationKey}
                  getUnreadCount={getUnreadCount}
                  searchText={searchText}
                  isCurrentMatch={isCurrentMatch}
                  itemRef={itemRef}
                  isBulkMode={isBulkMode}
                  isSelectedBulk={selectedBulkChats.has(activePhone)}
                  onToggleBulk={onToggleBulk}
                />
              );
            })}
            {bottomSpacerHeight > 0 && (
              <Box sx={{ height: `${bottomSpacerHeight}px` }} />
            )}
            {hasMore && (
              <LoadingMoreContainer>
                <CircularProgress size={20} />
                <Typography variant="body2" sx={{ ml: 1, color: "#667781" }}>
                  Carregando mais conversas...
                </Typography>
              </LoadingMoreContainer>
            )}
          </List>
        </Collapse>
      </ConversationGroup>
    );
  },
  (prevProps, nextProps) => {
    const searchTextChanged =
      (prevProps.searchText || "").trim() !==
      (nextProps.searchText || "").trim();
    if (searchTextChanged) {
      return false;
    }

    if (prevProps.currentSearchIndex !== nextProps.currentSearchIndex) {
      return false;
    }

    if (prevProps.campaign !== nextProps.campaign) {
      return false;
    }

    if (prevProps.conversations.length !== nextProps.conversations.length) {
      return false;
    }

    for (let i = 0; i < prevProps.conversations.length; i++) {
      if (prevProps.conversations[i] !== nextProps.conversations[i]) {
        return false;
      }
    }

    if (
      prevProps.expandedGroups[prevProps.campaign] !==
      nextProps.expandedGroups[nextProps.campaign]
    ) {
      return false;
    }

    if (
      prevProps.selectedConversation?.id !== nextProps.selectedConversation?.id
    ) {
      return false;
    }

    if (
      prevProps.hasMore !== nextProps.hasMore ||
      prevProps.loading !== nextProps.loading
    ) {
      return false;
    }

    if (prevProps.total !== nextProps.total) {
      return false;
    }

    if (
      prevProps.isBulkMode !== nextProps.isBulkMode ||
      prevProps.selectedBulkChats !== nextProps.selectedBulkChats
    ) {
      return false;
    }

    return true;
  },
);

CampaignGroup.displayName = "CampaignGroup";

const ChatSidebar = memo(() => {
  const [searchText, setSearchText] = useState("");
  const [effectiveSearchText, setEffectiveSearchText] = useState(""); // Texto de pesquisa efetivo (após Enter)
  const prevEffectiveSearchTextRef = useRef<string>("");
  const [currentSearchIndex, setCurrentSearchIndex] = useState<number>(-1);
  const searchMatchesRefs = useRef<
    Map<string, React.RefObject<HTMLDivElement>>
  >(new Map());
  const [activeFilter, setActiveFilter] = useState<FilterType>("all");
  const [selectedCampaignFilter, setSelectedCampaignFilter] =
    useState<string>(""); // Filtro por campanha específica
  const [showCampaignFilter, setShowCampaignFilter] = useState<boolean>(false); // Controlar exibição do filtro por campanha
  const [expandedGroups, setExpandedGroups] = useState<GroupState>({});

  // Estados para Ações em Lote (Bulk Actions)
  const [isBulkMode, setIsBulkMode] = useState(false);
  const [selectedBulkChats, setSelectedBulkChats] = useState<Set<string>>(
    new Set(),
  );
  const [isProcessingBulk, setIsProcessingBulk] = useState(false);
  const [bulkProgress, setBulkProgress] = useState(0);

  const toast = useToast();

  const handleToggleBulkMode = useCallback(() => {
    setIsBulkMode((prev) => {
      const next = !prev;
      if (!next) {
        setSelectedBulkChats(new Set());
      }
      return next;
    });
  }, []);

  const handleToggleBulkChat = useCallback((phone: string) => {
    setSelectedBulkChats((prev) => {
      const next = new Set(prev);
      if (next.has(phone)) {
        next.delete(phone);
      } else {
        next.add(phone);
      }
      return next;
    });
  }, []);
  const expandedGroupsRef = useRef<GroupState>({});
  const [pendingExpandGroups, setPendingExpandGroups] = useState<
    Record<string, boolean>
  >({});
  const [campaigns, setCampaigns] = useState<{ id: number; name: string }[]>(
    [],
  );
  const [campaignsLoading, setCampaignsLoading] = useState(false);
  const [campaignsPaginationLoading, setCampaignsPaginationLoading] =
    useState(false);
  const [campaignsPage, setCampaignsPage] = useState(1);
  const [, setCampaignsHasMore] = useState(true);
  const [, setCampaignsTotal] = useState(0);
  const [campaignsTotalPages, setCampaignsTotalPages] = useState(0);
  const [showBackToTop, setShowBackToTop] = useState(false);
  const [metricsByCampaign, setMetricsByCampaign] = useState<
    Map<string, ConversationMetrics>
  >(new Map());
  const [metricsLoadingByCampaign, setMetricsLoadingByCampaign] = useState<
    Map<string, boolean>
  >(new Map());
  const [metricsModalOpen, setMetricsModalOpen] = useState(false);
  const [metricsModalCampaign, setMetricsModalCampaign] = useState<string>("");
  const [metricsDownloadLoading, setMetricsDownloadLoading] = useState(false);
  const { selectedConversation, setSelectedConversation } = useChat();
  const { setSidebarType } = useSidebar();
  const { socket, account } = useAccount();
  const { handleApiCall } = useApiError();
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down(600));
  const isAdmin =
    account?.role === "ADMINISTRATOR" ||
    account?.role?.toUpperCase() === "ADMINISTRATOR";
  const updateTimeoutRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const contentContainerRef = useRef<HTMLDivElement>(null);
  const conversationListRef = useRef<HTMLUListElement>(null);

  // Flag para controlar se é o carregamento inicial
  const isInitialLoadRef = useRef(true);

  // Memoizar selectedConversation para evitar re-renders desnecessários
  const memoizedSelectedConversation = useMemo(
    () => selectedConversation,
    [selectedConversation],
  );

  // Carregar campanhas com paginação
  useEffect(() => {
    const loadCampaigns = async () => {
      // Evitar chamadas duplicadas
      if (campaignsPage === 1 && campaignsLoading) {
        return;
      }

      if (campaignsPage === 1) {
        setCampaignsLoading(true);
      } else {
        setCampaignsPaginationLoading(true);
      }

      await handleApiCall(
        async () => {
          return await apiService.get("/contacts/campaigns", {
            params: { page: campaignsPage, limit: 8 },
          });
        },
        {
          onSuccess: (response) => {
            if (response.status === "success" && (response as any).data) {
              const {
                campaigns: newCampaigns,
                total,
                hasMore,
                totalPages,
              } = (response as any).data as any;

              // Sempre substitui as campanhas (navegação por páginas)
              // Remove duplicatas baseado no ID da campanha
              const uniqueCampaigns = newCampaigns
                ? newCampaigns.filter(
                    (campaign: any, index: number, self: any[]) =>
                      index ===
                      self.findIndex((c: any) => c.id === campaign.id),
                  )
                : [];

              setCampaigns(uniqueCampaigns);
              setCampaignsTotal(total || 0);
              setCampaignsHasMore(hasMore || false);
              setCampaignsTotalPages(totalPages || 0);
            }
          },
          onError: () => {
            setCampaignsLoading(false);
            setCampaignsPaginationLoading(false);
          },
          fallbackErrorMessage: "Erro ao carregar campanhas. Tente novamente.",
          successMessage: undefined, // Não mostrar toast automático para carregamento
        },
      );

      setCampaignsLoading(false);
      setCampaignsPaginationLoading(false);
    };

    loadCampaigns();
  }, [campaignsPage, handleApiCall]);

  const handleOpenMetrics = useCallback(
    (campaignName: string) => {
      if (!isAdmin || !account?.cpf) {
        return;
      }

      setMetricsModalCampaign(campaignName);
      setMetricsModalOpen(true);

      if (metricsByCampaign.has(campaignName)) {
        return;
      }

      setMetricsLoadingByCampaign((prev) =>
        new Map(prev).set(campaignName, true),
      );
      handleApiCall(
        async () => {
          return await apiService.get("/contacts/metrics/campaign", {
            params: { cpf: account.cpf, campaignName },
          });
        },
        {
          onSuccess: (response) => {
            if (response.status === "success" && response.data) {
              setMetricsByCampaign((prev) => {
                const next = new Map(prev);
                next.set(campaignName, response.data as ConversationMetrics);
                return next;
              });
            }
          },
          onError: () => {
            setMetricsByCampaign((prev) => {
              const next = new Map(prev);
              next.delete(campaignName);
              return next;
            });
          },
          fallbackErrorMessage: "Erro ao carregar métricas da campanha.",
          successMessage: undefined,
        },
      ).finally(() => {
        setMetricsLoadingByCampaign((prev) =>
          new Map(prev).set(campaignName, false),
        );
      });
    },
    [account?.cpf, handleApiCall, isAdmin, metricsByCampaign],
  );

  const handleCloseMetrics = useCallback(() => {
    setMetricsModalOpen(false);
    setMetricsModalCampaign("");
  }, []);

  const handleDownloadCampaignMetrics = useCallback(async () => {
    if (!account?.cpf || !metricsModalCampaign) {
      return;
    }

    setMetricsDownloadLoading(true);
    try {
      const response = await handleApiCall<AxiosResponse<Blob>>(
        async () => {
          return await apiService.getRaw<Blob>(
            "/contacts/metrics/campaign/export",
            {
              params: { cpf: account.cpf, campaignName: metricsModalCampaign },
              responseType: "blob",
            },
          );
        },
        {
          showSuccessToast: false,
          fallbackErrorMessage: "Erro ao baixar métricas da campanha.",
        },
      );

      if (response?.data) {
        const blob = new Blob([response.data], {
          type: "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet",
        });
        const url = window.URL.createObjectURL(blob);
        const safeCampaignName = metricsModalCampaign.replace(
          /[\\/:*?"<>|]+/g,
          "-",
        );
        const link = document.createElement("a");
        link.href = url;
        link.download = `metricas-campanha-${safeCampaignName}.xlsx`;
        link.click();
        window.URL.revokeObjectURL(url);
      }
    } finally {
      setMetricsDownloadLoading(false);
    }
  }, [account?.cpf, handleApiCall, metricsModalCampaign]);

  // Função para ir para página anterior
  const goToPreviousPage = useCallback(() => {
    if (!campaignsLoading && !campaignsPaginationLoading && campaignsPage > 1) {
      setCampaignsPage((prev) => prev - 1);
    }
  }, [campaignsLoading, campaignsPaginationLoading, campaignsPage]);

  // Função para ir para próxima página
  const goToNextPage = useCallback(() => {
    if (
      !campaignsLoading &&
      !campaignsPaginationLoading &&
      campaignsPage < campaignsTotalPages
    ) {
      setCampaignsPage((prev) => prev + 1);
    }
  }, [
    campaignsLoading,
    campaignsPaginationLoading,
    campaignsPage,
    campaignsTotalPages,
  ]);

  // Função para detectar scroll e mostrar/esconder botão "Voltar ao topo"
  const handleScroll = useCallback(() => {
    // Verificar scroll no ContentContainer
    if (contentContainerRef.current) {
      const { scrollTop } = contentContainerRef.current;
      const shouldShow = scrollTop > 50;
      if (shouldShow) {
        setShowBackToTop(true);
        return;
      }
    }

    // Verificar scroll no ConversationList
    if (conversationListRef.current) {
      const { scrollTop } = conversationListRef.current;
      const shouldShow = scrollTop > 50;
      if (shouldShow) {
        setShowBackToTop(true);
        return;
      }
    }

    // Se não há scroll em nenhum dos dois, esconder o botão
    setShowBackToTop(false);
  }, []);

  // Função para voltar ao topo
  const scrollToTop = useCallback(() => {
    // Scrollar o ContentContainer
    if (contentContainerRef.current) {
      contentContainerRef.current.scrollTo({
        top: 0,
        behavior: "smooth",
      });
    }

    // Scrollar o ConversationList
    if (conversationListRef.current) {
      conversationListRef.current.scrollTo({
        top: 0,
        behavior: "smooth",
      });
    }
  }, []);

  // Usar o hook otimizado para gerenciar conversas por campanha
  const {
    campaignConversations,
    campaignLoadingStates,
    campaignHasMoreStates,
    campaignTotals,
    loadMoreForCampaign,
    ensureCampaignLoaded,
  } = useMultipleCampaigns({
    campaigns: campaigns || [], // Garantir que sempre seja um array
    eagerLoad: false, // Lazy: carrega uma campanha apenas quando expandida
    onConversationUpdate: useCallback(() => {
      // Atualizar grupos expandidos apenas no carregamento inicial
      if (isInitialLoadRef.current) {
        isInitialLoadRef.current = false;

        // No carregamento inicial, expandir apenas o grupo da conversa selecionada (se houver)
        if (memoizedSelectedConversation) {
          const selectedCampaign = memoizedSelectedConversation.campaign_name;
          if (selectedCampaign && !(selectedCampaign in expandedGroups)) {
            setExpandedGroups((prev) => ({
              ...prev,
              [selectedCampaign]: true,
            }));
          }
        }
      }
    }, [memoizedSelectedConversation, expandedGroups]),
    onNewMessage: useCallback((_phoneNumber: string, _message: MessageType) => {
      // Callback para notificar quando uma nova mensagem chega
    }, []),
  });

  // Manter ref do estado de expansão para callbacks estáveis
  useEffect(() => {
    expandedGroupsRef.current = expandedGroups;
  }, [expandedGroups]);

  // Clique de campanha:
  // - se já estiver carregada: expande/colapsa na hora
  // - se ainda não estiver carregada: carrega primeiro e expande assim que terminar (evita “clique duplo”)
  const handleToggleGroup = useCallback(
    (campaignName: string) => {
      const isExpanded = !!expandedGroupsRef.current[campaignName];

      if (isExpanded) {
        setExpandedGroups((prev) => ({ ...prev, [campaignName]: false }));
        setPendingExpandGroups((prev) => {
          if (!(campaignName in prev)) return prev;
          const next = { ...prev };
          delete next[campaignName];
          return next;
        });
        return;
      }

      const isLoaded = campaignTotals.has(campaignName);
      if (isLoaded) {
        setExpandedGroups((prev) => ({ ...prev, [campaignName]: true }));
        return;
      }

      setPendingExpandGroups((prev) => ({ ...prev, [campaignName]: true }));
      ensureCampaignLoaded(campaignName);
    },
    [campaignTotals, ensureCampaignLoaded],
  );

  // Expande automaticamente após carregar a primeira página (50) da campanha clicada
  useEffect(() => {
    const pendingCampaigns = Object.entries(pendingExpandGroups)
      .filter(([, v]) => v)
      .map(([k]) => k);

    if (pendingCampaigns.length === 0) return;

    // Abrir os grupos que já terminaram de carregar
    setExpandedGroups((prev) => {
      let next: GroupState | null = null;

      for (const campaignName of pendingCampaigns) {
        const loading = campaignLoadingStates.get(campaignName) || false;
        const loaded = campaignTotals.has(campaignName);
        if (loaded && !loading) {
          if (!prev[campaignName]) {
            if (!next) next = { ...prev };
            next[campaignName] = true;
          }
        }
      }

      return next ?? prev;
    });

    // Remover da fila os que já carregaram
    setPendingExpandGroups((prev) => {
      let next: Record<string, boolean> | null = null;

      for (const campaignName of pendingCampaigns) {
        const loading = campaignLoadingStates.get(campaignName) || false;
        const loaded = campaignTotals.has(campaignName);
        if (loaded && !loading) {
          if (campaignName in prev) {
            if (!next) next = { ...prev };
            delete next[campaignName];
          }
        }
      }

      return next ?? prev;
    });
  }, [pendingExpandGroups, campaignLoadingStates, campaignTotals]);

  // Garantir carregamento lazy quando um grupo é expandido
  useEffect(() => {
    Object.entries(expandedGroups).forEach(([campaignName, isExpanded]) => {
      if (isExpanded) {
        ensureCampaignLoaded(campaignName);
      }
    });
  }, [expandedGroups, ensureCampaignLoaded]);

  // Garantir carregamento da campanha da conversa selecionada ao montar/trocar seleção
  useEffect(() => {
    const selectedCampaign = memoizedSelectedConversation?.campaign_name;
    if (selectedCampaign) {
      ensureCampaignLoaded(selectedCampaign);
    }
  }, [memoizedSelectedConversation, ensureCampaignLoaded]);

  // Adicionar listener de scroll para detectar quando mostrar o botão "Voltar ao topo"
  useEffect(() => {
    const contentContainer = contentContainerRef.current;
    const conversationList = conversationListRef.current;

    if (contentContainer) {
      contentContainer.addEventListener("scroll", handleScroll);
    }

    if (conversationList) {
      conversationList.addEventListener("scroll", handleScroll);
    }

    return () => {
      if (contentContainer) {
        contentContainer.removeEventListener("scroll", handleScroll);
      }
      if (conversationList) {
        conversationList.removeEventListener("scroll", handleScroll);
      }
    };
  }, [handleScroll]);

  // Usar diretamente o hook useConversationReadStatus com as conversas atuais
  const allConversations = useMemo(() => {
    const conversations: ConversationData[] = [];
    campaignConversations.forEach((campaignConversations) => {
      conversations.push(...campaignConversations);
    });

    return conversations;
  }, [campaignConversations, campaigns]);

  const { getUnreadCount, updateConversationReadStatus } =
    useConversationReadStatus({
      socket: socket,
      conversations: allConversations,
    });

  // Listener para evento de mensagens marcadas como lidas
  useEffect(() => {
    const handleAllMessagesRead = (event: CustomEvent) => {
      const { phoneNumber } = event.detail;
      if (phoneNumber) {
        const normalizedPhone = normalizePhoneNumber(phoneNumber);

        // Limpar timeout anterior se existir
        if (updateTimeoutRef.current) {
          clearTimeout(updateTimeoutRef.current);
        }

        // Forçar atualização do contador para esta conversa
        updateTimeoutRef.current = setTimeout(() => {
          updateConversationReadStatus(normalizedPhone);
        }, 100);
      }
    };

    window.addEventListener(
      "allMessagesRead",
      handleAllMessagesRead as (event: Event) => void,
    );

    return () => {
      window.removeEventListener(
        "allMessagesRead",
        handleAllMessagesRead as (event: Event) => void,
      );
      // Limpar timeout se existir
      if (updateTimeoutRef.current) {
        clearTimeout(updateTimeoutRef.current);
      }
    };
  }, [updateConversationReadStatus, allConversations.length]);

  // Listener para atualizações do estado da IA
  useEffect(() => {
    if (!socket?.current) return;

    const handleAIStateUpdate = (data: any) => {
      const { phoneNumber, isAIActive } = data;
      if (phoneNumber) {
        // Atualizar o estado da IA na conversa correspondente
        const updatedConversations = allConversations.map((conv) => {
          const convPhone = conv.valid_telephone || conv.telephone;
          if (convPhone === phoneNumber) {
            return {
              ...conv,
              ai_state: {
                ...conv.ai_state,
                isAIActive,
                updatedAt: new Date(),
              },
            };
          }
          return conv;
        });

        // Atualizar as conversas se houver mudanças
        if (
          JSON.stringify(updatedConversations) !==
          JSON.stringify(allConversations)
        ) {
          // Forçar re-render das conversas filtradas
          // Como estamos usando useMemo, isso será refletido automaticamente
        }
      }
    };

    socket.current.on("aiStateUpdate", handleAIStateUpdate);

    return () => {
      if (socket?.current) {
        socket.current.off("aiStateUpdate", handleAIStateUpdate);
      }
    };
  }, [socket, allConversations]);

  // Memoizar props para ConversationItemMemo
  const memoizedHandleConversationClick = useCallback(
    (conversation: ConversationData) => {
      setSelectedConversation(conversation);

      // No mobile, ocultar a sidebar de conversas após selecionar uma conversa
      if (isMobile) {
        setSidebarType(null);
      }
    },
    [setSelectedConversation, isMobile, setSidebarType],
  );

  const memoizedGetConversationKey = useCallback(
    (conversation: ConversationData, index: number) => {
      // Garantir chave única combinando ID, telefone, campanha e informações da última mensagem
      const conversationId =
        conversation.id ||
        conversation.valid_telephone ||
        conversation.telephone ||
        "unknown";
      const campaignName = conversation.campaign_name || "unknown";
      const lastMessageId = conversation.last_message?._id || "no-message";
      const lastMessageTimestamp =
        conversation.last_message?.timestamp || "no-timestamp";
      return `conversation-${conversationId}-${campaignName}-${lastMessageId}-${lastMessageTimestamp}-${index}`;
    },
    [],
  );

  // Função para filtrar conversas baseado em searchText e activeFilter
  const filterConversations = useCallback(
    (conversations: ConversationData[]): ConversationData[] => {
      let filtered = [...conversations];

      // Aplicar filtro de texto de pesquisa (usar effectiveSearchText - só pesquisa após Enter)
      if (effectiveSearchText.trim()) {
        const searchLower = effectiveSearchText.toLowerCase().trim();
        filtered = filtered.filter((conv) => {
          // Buscar por nome do cliente
          const name = (conv.valid_name || conv.name || "").toLowerCase();
          // Buscar por nome da campanha
          const campaign = (conv.campaign_name || "").toLowerCase();
          // Buscar por telefone
          const phone = (conv.valid_telephone || conv.telephone || "").replace(
            /\D/g,
            "",
          );
          const searchPhone = searchLower.replace(/\D/g, "");

          // Verificar correspondência exata (não apenas substring de uma letra)
          // IMPORTANTE: Verificar se o termo pesquisado realmente corresponde
          const nameMatches =
            searchLower.length > 0 && name.includes(searchLower);
          const campaignMatches =
            searchLower.length > 0 && campaign.includes(searchLower);
          const phoneMatches =
            searchPhone.length > 0 && phone.includes(searchPhone);

          // IMPORTANTE: Para exibir na lista, incluímos conversas que correspondem por:
          // - Nome do cliente, OU
          // - Telefone, OU
          // - Nome da campanha (para mostrar todas as conversas da campanha que corresponde)
          // Mas para o contador, vamos filtrar apenas as que realmente têm o termo no nome ou telefone
          // Isso será feito no searchMatches

          // Retornar true para exibir na lista se pelo menos uma correspondência for encontrada
          return nameMatches || phoneMatches || campaignMatches;
        });
      }

      if (activeFilter === "unread") {
        filtered = filtered.filter((conv) => {
          const unreadCount = getUnreadCount(conv);
          return unreadCount > 0;
        });
      } else if (activeFilter === "ai_disabled") {
        filtered = filtered.filter((conv) => {
          return conv.ai_state?.isAIActive === false;
        });
      } else if (activeFilter === "ai_enabled") {
        filtered = filtered.filter((conv) => {
          return conv.ai_state?.isAIActive !== false; // true ou undefined
        });
      } else if (activeFilter === "last_client_response") {
        filtered = filtered.filter((conv) => {
          return conv.last_message?.sentBy === "client";
        });
      }

      return filtered;
    },
    [effectiveSearchText, activeFilter, getUnreadCount],
  );

  // Função para obter a lista exata de conversas visíveis com base nos filtros e pesquisa ativa
  const getFilteredConversations = useCallback(() => {
    const list: ConversationData[] = [];
    if (campaigns && campaigns.length > 0) {
      const campaignNames = campaigns.map((c) => c.name);
      const visibleCampaigns = campaignNames.filter((campaignName) => {
        if (selectedCampaignFilter && campaignName !== selectedCampaignFilter)
          return false;
        if (effectiveSearchText.trim()) {
          const searchLower = effectiveSearchText.toLowerCase().trim();
          const campaignNameLower = campaignName.toLowerCase();
          if (campaignNameLower.includes(searchLower)) return true;
          const campaignConversationsList =
            campaignConversations.get(campaignName) || [];
          const filtered = filterConversations(campaignConversationsList);
          return filtered.length > 0;
        }
        const isLoaded = campaignTotals.has(campaignName);
        if (activeFilter !== "all" && isLoaded) {
          const campaignConversationsList =
            campaignConversations.get(campaignName) || [];
          const filtered = filterConversations(campaignConversationsList);
          return filtered.length > 0;
        }
        return true;
      });

      visibleCampaigns.forEach((campaignName) => {
        const campaignConversationsList =
          campaignConversations.get(campaignName) || [];
        const filteredConversations = filterConversations(
          campaignConversationsList,
        );
        list.push(...filteredConversations);
      });
    }
    return list;
  }, [
    campaigns,
    selectedCampaignFilter,
    effectiveSearchText,
    campaignConversations,
    activeFilter,
    filterConversations,
    campaignTotals,
  ]);

  const handleSelectAll = useCallback(() => {
    const visible = getFilteredConversations();
    const newSelection = new Set<string>();
    visible.forEach((conv) => {
      const phone = conv.valid_telephone || conv.telephone;
      if (phone) {
        newSelection.add(phone);
      }
    });
    setSelectedBulkChats(newSelection);
  }, [getFilteredConversations]);

  const handleDeselectAll = useCallback(() => {
    setSelectedBulkChats(new Set());
  }, []);

  const handleBulkAction = useCallback(
    async (actionType: "read" | "enable_ai" | "disable_ai") => {
      if (selectedBulkChats.size === 0) {
        toast.warning("Nenhuma conversa selecionada.");
        return;
      }

      setIsProcessingBulk(true);
      setBulkProgress(0);

      const chatArray = Array.from(selectedBulkChats);
      const totalChats = chatArray.length;
      let processedCount = 0;

      const batchSize = 3;
      const delayMs = 100;

      try {
        for (let i = 0; i < totalChats; i += batchSize) {
          const batch = chatArray.slice(i, i + batchSize);

          await Promise.all(
            batch.map(async (phone) => {
              try {
                if (actionType === "read") {
                  await markAllMessagesAsRead(
                    phone,
                    account?.id || "",
                    account?.username || account?.name || "",
                  );
                  window.dispatchEvent(
                    new CustomEvent("allMessagesRead", {
                      detail: { phoneNumber: phone },
                    }),
                  );
                } else if (actionType === "enable_ai") {
                  await toggleAIState(phone, true);
                  window.dispatchEvent(
                    new CustomEvent("aiStateUpdate", {
                      detail: { phoneNumber: phone, isAIActive: true },
                    }),
                  );
                } else if (actionType === "disable_ai") {
                  await toggleAIState(
                    phone,
                    false,
                    "Desativado em lote pelo administrador",
                  );
                  window.dispatchEvent(
                    new CustomEvent("aiStateUpdate", {
                      detail: { phoneNumber: phone, isAIActive: false },
                    }),
                  );
                }
              } catch (err) {
                console.error(`Erro ao processar conversa ${phone}:`, err);
              } finally {
                processedCount++;
                setBulkProgress(
                  Math.round((processedCount / totalChats) * 100),
                );
              }
            }),
          );

          if (i + batchSize < totalChats) {
            await new Promise((resolve) => setTimeout(resolve, delayMs));
          }
        }

        toast.success(
          `Ação executada com sucesso para ${totalChats} conversas!`,
        );
        setSelectedBulkChats(new Set());
        setIsBulkMode(false);
      } catch (err) {
        console.error("Erro na execução em lote:", err);
        toast.error("Ocorreu um erro ao processar as ações em lote.");
      } finally {
        setIsProcessingBulk(false);
        setBulkProgress(0);
      }
    },
    [selectedBulkChats, account, toast],
  );

  // Expandir e carregar automaticamente campanhas que contêm resultados da pesquisa
  useEffect(() => {
    if (effectiveSearchText.trim() && campaigns.length > 0) {
      const searchLower = effectiveSearchText.toLowerCase().trim();
      const campaignsWithResults = new Set<string>();
      const campaignsToLoad = new Set<string>();

      campaigns.forEach((campaign) => {
        const campaignName = campaign.name;

        // Se há filtro por campanha selecionado, pular campanhas que não correspondem
        if (selectedCampaignFilter && campaignName !== selectedCampaignFilter) {
          return;
        }

        const campaignNameLower = campaignName.toLowerCase();

        // Verificar se a pesquisa corresponde ao nome da campanha
        const matchesCampaignName = campaignNameLower.includes(searchLower);

        // IMPORTANTE: Mesmo que o nome da campanha corresponda, precisamos verificar
        // se há conversas que realmente correspondem à pesquisa
        const campaignConversationsList =
          campaignConversations.get(campaignName) || [];
        const filtered = filterConversations(campaignConversationsList);

        // Só adicionar a campanha se houver conversas que correspondem OU se o nome da campanha corresponde
        // Mas sempre filtrar as conversas que realmente correspondem
        if (matchesCampaignName || filtered.length > 0) {
          campaignsWithResults.add(campaignName);
          // Se não há conversas carregadas e o nome corresponde, precisamos carregar
          if (campaignConversationsList.length === 0 && matchesCampaignName) {
            campaignsToLoad.add(campaignName);
          }
        }
      });

      // Carregar conversas das campanhas que precisam ser carregadas
      campaignsToLoad.forEach((campaignName) => {
        ensureCampaignLoaded(campaignName);
      });

      // Expandir campanhas com resultados
      if (campaignsWithResults.size > 0) {
        setExpandedGroups((prev) => {
          const newGroups = { ...prev };
          campaignsWithResults.forEach((campaignName) => {
            newGroups[campaignName] = true;
          });
          return newGroups;
        });
      }
    } else if (!effectiveSearchText.trim()) {
      // Só colapsar quando houver transição de "com pesquisa" -> "sem pesquisa".
      // Caso contrário, cada atualização de campaignConversations/campaigns faria colapsar tudo,
      // impedindo expandir automaticamente após o lazy-load.
      const prev = prevEffectiveSearchTextRef.current.trim();
      if (prev) {
        setExpandedGroups({});
      }
    }

    prevEffectiveSearchTextRef.current = effectiveSearchText;
  }, [
    effectiveSearchText,
    campaigns,
    campaignConversations,
    filterConversations,
    ensureCampaignLoaded,
    selectedCampaignFilter,
  ]);

  // Memoizar o tamanho do Map para evitar re-renderizações desnecessárias
  const campaignConversationsSize = useMemo(() => {
    return campaignConversations.size;
  }, [campaignConversations.size]);

  // Coletar todos os matches para navegação
  const searchMatches = useMemo(() => {
    const searchTextTrimmed = effectiveSearchText.trim();
    if (!searchTextTrimmed) {
      return [];
    }

    const searchLower = searchTextTrimmed.toLowerCase();
    const matches: ConversationData[] = [];
    const campaignMatchesSet = new Set<string>(); // Para contar campanhas que correspondem

    campaignConversations.forEach((conversations, campaignName) => {
      // Se há filtro por campanha selecionado, pular campanhas que não correspondem
      if (selectedCampaignFilter && campaignName !== selectedCampaignFilter) {
        return;
      }

      const filtered = filterConversations(conversations);

      // Verificar se a campanha corresponde ao termo pesquisado
      const campaignNameLower = campaignName.toLowerCase();
      const campaignMatches =
        searchLower.length > 0 && campaignNameLower.includes(searchLower);

      if (campaignMatches) {
        campaignMatchesSet.add(campaignName);
      }

      matches.push(...filtered);
    });

    // Filtrar conversas que realmente correspondem ao termo (nome do cliente ou telefone)
    const conversationMatches = matches.filter((match) => {
      const name = (match.valid_name || match.name || "").toLowerCase();
      const phone = (match.valid_telephone || match.telephone || "").replace(
        /\D/g,
        "",
      );
      const searchPhone = searchLower.replace(/\D/g, "");

      const nameMatches = searchLower.length > 0 && name.includes(searchLower);
      const phoneMatches =
        searchPhone.length > 0 && phone.includes(searchPhone);

      return nameMatches || phoneMatches;
    });

    // IMPORTANTE: O contador deve mostrar as ocorrências do termo pesquisado, seja em campanhas ou conversas
    // Se uma campanha corresponde, isso conta como 1 ocorrência (a campanha)
    // Se uma conversa corresponde (nome ou telefone), isso conta como 1 ocorrência (a conversa)
    // Para navegação, vamos usar apenas as conversas que correspondem, mas vamos adicionar
    // as campanhas que correspondem como "matches" virtuais para o contador

    // Criar um array com as conversas que correspondem + campanhas que correspondem
    // Para navegação, vamos usar apenas as conversas reais
    const validMatches = conversationMatches;

    // Para navegação, retornar apenas as conversas que correspondem
    // Mas o contador será ajustado para incluir também as campanhas
    return validMatches;
  }, [
    effectiveSearchText,
    campaignConversations,
    campaignConversationsSize,
    filterConversations,
    selectedCampaignFilter,
  ]);

  // Calcular total de ocorrências (conversas + campanhas) para o contador
  // E criar lista ordenada de campanhas que correspondem
  const { totalSearchOccurrences, matchingCampaigns } = useMemo(() => {
    const searchTextTrimmed = effectiveSearchText.trim();
    if (!searchTextTrimmed) {
      return { totalSearchOccurrences: 0, matchingCampaigns: [] };
    }

    const searchLower = searchTextTrimmed.toLowerCase();
    let total = 0;
    const matchingCampaignsList: string[] = [];

    // Contar conversas que correspondem (nome do cliente ou telefone)
    campaignConversations.forEach((conversations, campaignName) => {
      // Se há filtro por campanha selecionado, pular campanhas que não correspondem
      if (selectedCampaignFilter && campaignName !== selectedCampaignFilter) {
        return;
      }

      conversations.forEach((conv) => {
        const name = (conv.valid_name || conv.name || "").toLowerCase();
        const phone = (conv.valid_telephone || conv.telephone || "").replace(
          /\D/g,
          "",
        );
        const searchPhone = searchLower.replace(/\D/g, "");

        const nameMatches =
          searchLower.length > 0 && name.includes(searchLower);
        const phoneMatches =
          searchPhone.length > 0 && phone.includes(searchPhone);

        if (nameMatches || phoneMatches) {
          total++;
        }
      });
    });

    // Contar e listar campanhas que correspondem
    campaigns.forEach((campaign) => {
      // Se há filtro por campanha selecionado, pular campanhas que não correspondem
      if (selectedCampaignFilter && campaign.name !== selectedCampaignFilter) {
        return;
      }

      const campaignNameLower = campaign.name.toLowerCase();
      if (searchLower.length > 0 && campaignNameLower.includes(searchLower)) {
        total++;
        matchingCampaignsList.push(campaign.name);
      }
    });

    return {
      totalSearchOccurrences: total,
      matchingCampaigns: matchingCampaignsList,
    };
  }, [
    effectiveSearchText,
    campaignConversations,
    campaigns,
    selectedCampaignFilter,
  ]);

  // Resetar índice quando a pesquisa mudar
  useEffect(() => {
    if (effectiveSearchText.trim()) {
      setCurrentSearchIndex(0);
    } else {
      setCurrentSearchIndex(-1);
    }
  }, [effectiveSearchText]);

  // Garantir que a campanha selecionada no filtro esteja sempre disponível no Select
  // mesmo que não esteja na página atual
  const availableCampaignsForSelect = useMemo(() => {
    if (!selectedCampaignFilter) {
      return campaigns;
    }

    // Verificar se a campanha selecionada já está na lista
    const isSelectedCampaignInList = campaigns.some(
      (c) => c.name === selectedCampaignFilter,
    );

    if (isSelectedCampaignInList) {
      return campaigns;
    }

    // Se não estiver, adicionar a campanha selecionada à lista
    // (usando um objeto temporário com id: -1 para indicar que não está na página atual)
    return [{ id: -1, name: selectedCampaignFilter }, ...campaigns];
  }, [campaigns, selectedCampaignFilter]);

  // Expandir automaticamente a campanha quando o filtro for selecionado
  useEffect(() => {
    if (selectedCampaignFilter) {
      setExpandedGroups((prev) => ({
        ...prev,
        [selectedCampaignFilter]: true,
      }));
      // Garantir que a campanha seja carregada
      ensureCampaignLoaded(selectedCampaignFilter);
    }
  }, [selectedCampaignFilter, ensureCampaignLoaded]);

  // Funções de navegação
  const navigateToNext = useCallback(() => {
    if (totalSearchOccurrences === 0) return;
    setCurrentSearchIndex((prev) => (prev + 1) % totalSearchOccurrences);
  }, [totalSearchOccurrences]);

  const navigateToPrevious = useCallback(() => {
    if (totalSearchOccurrences === 0) return;
    setCurrentSearchIndex(
      (prev) => (prev - 1 + totalSearchOccurrences) % totalSearchOccurrences,
    );
  }, [totalSearchOccurrences]);

  // Scroll para o item atual e expandir campanha se necessário
  useEffect(() => {
    if (currentSearchIndex < 0 || totalSearchOccurrences === 0) return;

    // Se o índice aponta para uma conversa
    if (currentSearchIndex < searchMatches.length && searchMatches.length > 0) {
      const conversation = searchMatches[currentSearchIndex];
      const key = memoizedGetConversationKey(conversation, currentSearchIndex);
      const ref = searchMatchesRefs.current.get(key);

      if (ref?.current) {
        setTimeout(() => {
          ref.current?.scrollIntoView({
            behavior: "smooth",
            block: "center",
          });
        }, 100);
      }
    }
    // Se o índice aponta para uma campanha (índice >= número de conversas)
    else if (currentSearchIndex >= searchMatches.length) {
      const campaignIndex = currentSearchIndex - searchMatches.length;
      if (campaignIndex < matchingCampaigns.length) {
        const campaignName = matchingCampaigns[campaignIndex];

        // Expandir a campanha se não estiver expandida
        setExpandedGroups((prev) => {
          if (!prev[campaignName]) {
            return { ...prev, [campaignName]: true };
          }
          return prev;
        });

        // Fazer scroll para o header da campanha
        setTimeout(() => {
          // Procurar o elemento do header da campanha
          const campaignHeader = document.querySelector(
            `[data-campaign-name="${campaignName}"]`,
          );
          if (campaignHeader) {
            campaignHeader.scrollIntoView({
              behavior: "smooth",
              block: "center",
            });
          }
        }, 100);
      }
    }
  }, [
    currentSearchIndex,
    searchMatches,
    totalSearchOccurrences,
    matchingCampaigns,
    memoizedGetConversationKey,
    setExpandedGroups,
  ]);

  // Memoizar a renderização do ConversationList para evitar re-renders desnecessários
  const conversationListContent = useMemo(() => {
    if (campaignsLoading) {
      return (
        <Box sx={{ display: "flex", justifyContent: "center", p: 2 }}>
          <CircularProgress size={24} />
        </Box>
      );
    }

    // Quando não houver conversas carregadas ainda, mas houver campanhas, listar cabeçalhos das campanhas (lazy)
    if (campaigns && campaigns.length > 0) {
      // Ordenar pelo nome para consistência
      const campaignNames = campaigns
        .map((c) => c.name)
        .sort((a, b) => a.localeCompare(b));

      // Filtrar campanhas que têm conversas visíveis após filtros
      const visibleCampaigns = campaignNames.filter((campaignName) => {
        // Se há filtro por campanha selecionado, mostrar apenas essa campanha
        if (selectedCampaignFilter && campaignName !== selectedCampaignFilter) {
          return false;
        }

        // Se há pesquisa, verificar se a campanha ou suas conversas correspondem
        if (effectiveSearchText.trim()) {
          const searchLower = effectiveSearchText.toLowerCase().trim();
          const campaignNameLower = campaignName.toLowerCase();

          // Verificar se o nome da campanha corresponde à pesquisa
          if (campaignNameLower.includes(searchLower)) {
            return true;
          }

          // Verificar se há conversas carregadas que correspondem
          const campaignConversationsList =
            campaignConversations.get(campaignName) || [];
          const filtered = filterConversations(campaignConversationsList);
          return filtered.length > 0;
        }

        // Sem pesquisa, se houver filtro ativo (diferente de 'all') e a campanha estiver carregada,
        // ocultamos a campanha caso não haja conversas correspondentes ao filtro.
        // Se a campanha NÃO estiver carregada (isLoaded === false), mantemos visível para que o usuário possa expandi-la e carregar.
        const isLoaded = campaignTotals.has(campaignName);
        if (activeFilter !== "all" && isLoaded) {
          const campaignConversationsList =
            campaignConversations.get(campaignName) || [];
          const filtered = filterConversations(campaignConversationsList);
          return filtered.length > 0;
        }

        return true;
      });

      if (visibleCampaigns.length === 0 && effectiveSearchText.trim()) {
        return (
          <Box sx={{ p: 2, textAlign: "center", color: "#667781" }}>
            Nenhuma conversa encontrada para &quot;{effectiveSearchText}&quot;
          </Box>
        );
      }

      return visibleCampaigns.map((campaignName) => {
        const campaignConversationsList =
          campaignConversations.get(campaignName) || [];
        const filteredConversations = filterConversations(
          campaignConversationsList,
        );
        const isLoaded = campaignTotals.has(campaignName);

        return (
          <CampaignGroup
            key={campaignName}
            campaign={campaignName}
            conversations={filteredConversations}
            expandedGroups={expandedGroups}
            selectedConversation={memoizedSelectedConversation}
            onConversationClick={memoizedHandleConversationClick}
            getConversationKey={memoizedGetConversationKey}
            getUnreadCount={getUnreadCount}
            hasMore={campaignHasMoreStates.get(campaignName) || false}
            loading={campaignLoadingStates.get(campaignName) || false}
            onLoadMore={() => loadMoreForCampaign(campaignName)}
            total={campaignTotals.get(campaignName)}
            isLoaded={isLoaded}
            onToggleGroup={handleToggleGroup}
            searchText={effectiveSearchText}
            currentSearchIndex={currentSearchIndex}
            searchMatches={searchMatches}
            searchMatchesRefs={searchMatchesRefs}
            matchingCampaigns={matchingCampaigns}
            showMetrics={isAdmin}
            onMetricsClick={handleOpenMetrics}
            isBulkMode={isBulkMode}
            selectedBulkChats={selectedBulkChats}
            onToggleBulk={handleToggleBulkChat}
          />
        );
      });
    }

    // Sem campanhas disponíveis
    return (
      <Box sx={{ p: 2, textAlign: "center", color: "#667781" }}>
        {effectiveSearchText.trim()
          ? "Nenhuma conversa encontrada"
          : "Nenhuma conversa disponível"}
      </Box>
    );
  }, [
    campaignsLoading,
    campaigns,
    campaignConversations,
    expandedGroups,
    memoizedSelectedConversation,
    memoizedGetConversationKey,
    memoizedHandleConversationClick,
    getUnreadCount,
    campaignHasMoreStates,
    campaignLoadingStates,
    campaignTotals,
    loadMoreForCampaign,
    effectiveSearchText,
    activeFilter,
    filterConversations,
    currentSearchIndex,
    searchMatches,
    searchMatchesRefs,
  ]);

  return (
    <>
      <Container>
        <Box sx={{ height: "60px", flexShrink: 0 }}>
          <Header title="Conversas" />
        </Box>
        <ContentContainer ref={contentContainerRef}>
          <SearchContainer>
            <SearchRow>
              <SearchInputWrapper
                hasActiveSearch={effectiveSearchText.trim().length > 0}
                hasUnsearchedText={
                  searchText.trim().length > 0 &&
                  searchText.trim() !== effectiveSearchText.trim()
                }
              >
                <IconButton
                  size="small"
                  onClick={() => {
                    if (searchText.trim()) {
                      setEffectiveSearchText(searchText.trim());
                    }
                  }}
                  disabled={
                    !searchText.trim() ||
                    searchText.trim() === effectiveSearchText.trim()
                  }
                  sx={{
                    padding: "4px",
                    color:
                      searchText.trim() &&
                      searchText.trim() !== effectiveSearchText.trim()
                        ? "#ff9800"
                        : effectiveSearchText.trim()
                          ? "#008069"
                          : "#54656f",
                    "&:hover": {
                      backgroundColor: "rgba(0, 0, 0, 0.05)",
                    },
                    "&:disabled": {
                      opacity: 0.4,
                    },
                    transition: "color 0.2s ease",
                  }}
                  title={
                    searchText.trim() &&
                    searchText.trim() !== effectiveSearchText.trim()
                      ? "Pesquisar (ou pressione Enter)"
                      : "Pesquisar"
                  }
                >
                  <Search sx={{ fontSize: 20 }} />
                </IconButton>
                <SearchInput
                  id="conversation-search"
                  name="conversationSearch"
                  placeholder={
                    searchText.trim() &&
                    searchText.trim() !== effectiveSearchText.trim()
                      ? "Pressione Enter para pesquisar..."
                      : "Pesquisar por nome, campanha ou telefone..."
                  }
                  value={searchText}
                  onChange={(e) => setSearchText(e.target.value)}
                  onKeyDown={(e) => {
                    if (e.key === "Enter") {
                      const hasNewSearchText =
                        searchText.trim() !== effectiveSearchText.trim();

                      if (hasNewSearchText) {
                        // Se o texto digitado é diferente do pesquisado, efetivar nova pesquisa
                        e.preventDefault();
                        setEffectiveSearchText(searchText.trim());
                      } else if (
                        e.shiftKey &&
                        effectiveSearchText.trim() &&
                        totalSearchOccurrences > 0
                      ) {
                        // Shift+Enter: navegar para anterior (se não há novo texto)
                        navigateToPrevious();
                      } else if (
                        effectiveSearchText.trim() &&
                        totalSearchOccurrences > 0
                      ) {
                        // Enter: navegar para próximo (se não há novo texto e já houver pesquisa)
                        navigateToNext();
                      } else {
                        // Enter: efetivar pesquisa (se não há pesquisa ativa)
                        e.preventDefault();
                        setEffectiveSearchText(searchText.trim());
                      }
                    } else if (e.key === "Escape") {
                      // ESC: limpar pesquisa
                      setSearchText("");
                      setEffectiveSearchText("");
                    }
                  }}
                  inputProps={{
                    "aria-label": "Pesquisar conversas",
                    style: {
                      paddingRight:
                        effectiveSearchText.trim() && totalSearchOccurrences > 0
                          ? "140px"
                          : searchText
                            ? "32px"
                            : "0px",
                    },
                  }}
                  sx={{
                    flex: 1,
                    "& input": {
                      "&::placeholder": {
                        opacity: 0.7,
                        fontSize: "13px",
                        transition: "opacity 0.2s ease",
                      },
                    },
                  }}
                />
                {searchText.trim() &&
                  searchText.trim() !== effectiveSearchText.trim() && (
                    <Typography
                      variant="caption"
                      sx={{
                        color: "#ff9800",
                        fontSize: "10px",
                        whiteSpace: "nowrap",
                        fontWeight: 500,
                        position: "absolute",
                        bottom: "-18px",
                        left: "8px",
                        backgroundColor: "#fff9e6",
                        padding: "2px 6px",
                        borderRadius: "4px",
                        border: "1px solid #ffc107",
                        zIndex: 2,
                      }}
                    >
                      Pressione Enter para pesquisar
                    </Typography>
                  )}
                {effectiveSearchText.trim() && totalSearchOccurrences > 0 && (
                  <Box
                    sx={{
                      position: "absolute",
                      right: searchText ? "32px" : "8px",
                      display: "flex",
                      alignItems: "center",
                      gap: 0.5,
                      pointerEvents: "auto",
                      zIndex: 1,
                    }}
                  >
                    <Typography
                      variant="caption"
                      sx={{
                        color: "#54656f",
                        fontSize: "11px",
                        whiteSpace: "nowrap",
                        fontWeight: 500,
                        mr: 0.5,
                        lineHeight: "24px",
                        display: "flex",
                        alignItems: "center",
                      }}
                    >
                      {currentSearchIndex + 1} / {totalSearchOccurrences}
                    </Typography>
                    <IconButton
                      size="small"
                      onClick={(e) => {
                        e.stopPropagation();
                        e.preventDefault();
                        navigateToPrevious();
                      }}
                      disabled={totalSearchOccurrences === 0}
                      sx={{
                        padding: "2px",
                        minWidth: "24px",
                        width: "24px",
                        height: "24px",
                        color: "#54656f",
                        "&:hover": {
                          backgroundColor: "rgba(0, 0, 0, 0.05)",
                        },
                        "&:disabled": {
                          opacity: 0.3,
                        },
                      }}
                      title="Anterior (Shift+Enter)"
                    >
                      <KeyboardArrowUp sx={{ fontSize: 16 }} />
                    </IconButton>
                    <IconButton
                      size="small"
                      onClick={(e) => {
                        e.stopPropagation();
                        e.preventDefault();
                        navigateToNext();
                      }}
                      disabled={totalSearchOccurrences === 0}
                      sx={{
                        padding: "2px",
                        minWidth: "24px",
                        width: "24px",
                        height: "24px",
                        color: "#54656f",
                        "&:hover": {
                          backgroundColor: "rgba(0, 0, 0, 0.05)",
                        },
                        "&:disabled": {
                          opacity: 0.3,
                        },
                      }}
                      title="Próximo (Enter)"
                    >
                      <KeyboardArrowDown sx={{ fontSize: 16 }} />
                    </IconButton>
                  </Box>
                )}
                {searchText && (
                  <IconButton
                    size="small"
                    onClick={() => {
                      setSearchText("");
                      setEffectiveSearchText("");
                    }}
                    sx={{
                      padding: "2px",
                      minWidth: "24px",
                      width: "24px",
                      height: "24px",
                      color: "#54656f",
                      "&:hover": {
                        backgroundColor: "rgba(0, 0, 0, 0.05)",
                      },
                    }}
                    title="Limpar pesquisa"
                  >
                    <Box
                      component="span"
                      sx={{
                        fontSize: "16px",
                        lineHeight: 1,
                        display: "flex",
                        alignItems: "center",
                        justifyContent: "center",
                      }}
                    >
                      ×
                    </Box>
                  </IconButton>
                )}
              </SearchInputWrapper>
              {/* Botão para expandir/colapsar filtro por campanha */}
              <IconButton
                size="small"
                onClick={() => setShowCampaignFilter(!showCampaignFilter)}
                sx={{
                  padding: "8px",
                  color: selectedCampaignFilter ? "#008069" : "#54656f",
                  backgroundColor: selectedCampaignFilter
                    ? "rgba(0, 128, 105, 0.1)"
                    : "transparent",
                  "&:hover": {
                    backgroundColor: selectedCampaignFilter
                      ? "rgba(0, 128, 105, 0.15)"
                      : "rgba(0, 0, 0, 0.05)",
                  },
                  transition: "all 0.2s ease",
                  flexShrink: 0,
                }}
                title={
                  selectedCampaignFilter
                    ? `Filtro: ${selectedCampaignFilter}`
                    : "Filtrar por campanha"
                }
              >
                <FilterList
                  sx={{
                    fontSize: 20,
                    transform: showCampaignFilter
                      ? "rotate(180deg)"
                      : "rotate(0deg)",
                    transition: "transform 0.2s ease",
                  }}
                />
              </IconButton>

              {/* Botão para Ações em Lote */}
              <IconButton
                size="small"
                onClick={handleToggleBulkMode}
                sx={{
                  padding: "8px",
                  color: isBulkMode ? "#008069" : "#54656f",
                  backgroundColor: isBulkMode
                    ? "rgba(0, 128, 105, 0.1)"
                    : "transparent",
                  "&:hover": {
                    backgroundColor: isBulkMode
                      ? "rgba(0, 128, 105, 0.15)"
                      : "rgba(0, 0, 0, 0.05)",
                  },
                  transition: "all 0.2s ease",
                  flexShrink: 0,
                  ml: 0.5,
                }}
                title={isBulkMode ? "Sair do modo em lote" : "Ações em lote"}
              >
                <PlaylistAddCheck sx={{ fontSize: 22 }} />
              </IconButton>
            </SearchRow>
            {/* Filtro por Campanha (colapsável) */}
            <CampaignFilterContainer in={showCampaignFilter}>
              <Box
                sx={{
                  display: "flex",
                  gap: 1.5,
                  flexWrap: "wrap",
                }}
              >
                <FormControl size="small" sx={{ minWidth: 180, flex: 1 }}>
                  <Select
                    value={selectedCampaignFilter}
                    onChange={(e) => setSelectedCampaignFilter(e.target.value)}
                    displayEmpty
                    sx={{
                      fontSize: "13px",
                      height: "40px",
                      borderRadius: "8px",
                      "& .MuiOutlinedInput-root": {
                        backgroundColor: "#fff",
                        "& fieldset": {
                          borderColor: "#e9edef",
                          borderRadius: "8px",
                        },
                        "&:hover fieldset": {
                          borderColor: "#008069",
                        },
                        "&.Mui-focused fieldset": {
                          borderColor: "#008069",
                        },
                      },
                      "& .MuiSelect-select": {
                        padding: "8px 32px 8px 12px",
                        color: "#111b21",
                      },
                      "& .MuiSelect-icon": {
                        color: "#54656f",
                      },
                    }}
                  >
                    <MenuItem value="">
                      <Typography sx={{ fontSize: "13px", color: "#667781" }}>
                        Todas as Campanhas
                      </Typography>
                    </MenuItem>
                    {availableCampaignsForSelect.map((campaign) => (
                      <MenuItem
                        key={
                          campaign.id === -1
                            ? `temp-${campaign.name}`
                            : campaign.id
                        }
                        value={campaign.name}
                      >
                        <Typography sx={{ fontSize: "13px" }}>
                          {campaign.name}
                          {campaign.id === -1 && (
                            <Typography
                              component="span"
                              sx={{
                                fontSize: "10px",
                                color: "#667781",
                                ml: 0.5,
                                fontStyle: "italic",
                              }}
                            >
                              (outra página)
                            </Typography>
                          )}
                        </Typography>
                      </MenuItem>
                    ))}
                  </Select>
                </FormControl>
              </Box>
            </CampaignFilterContainer>
          </SearchContainer>
          <FilterContainer>
            <FilterBadgeBase
              isActive={activeFilter === "all"}
              onClick={() => setActiveFilter("all")}
            >
              Todos
            </FilterBadgeBase>
            <FilterBadgeBase
              isActive={activeFilter === "unread"}
              onClick={() => setActiveFilter("unread")}
            >
              Não lidas
            </FilterBadgeBase>
            <FilterBadgeBase
              isActive={activeFilter === "last_client_response"}
              onClick={() => setActiveFilter("last_client_response")}
            >
              Última Resposta
            </FilterBadgeBase>
            <FilterBadgeBase
              isActive={activeFilter === "ai_disabled"}
              onClick={() => setActiveFilter("ai_disabled")}
            >
              IA desativada
            </FilterBadgeBase>
            <FilterBadgeBase
              isActive={activeFilter === "ai_enabled"}
              onClick={() => setActiveFilter("ai_enabled")}
            >
              IA ativada
            </FilterBadgeBase>
          </FilterContainer>

          <ConversationList ref={conversationListRef}>
            {campaignsLoading && (
              <Box sx={{ p: 1 }}>
                {Array.from({ length: 8 }).map((_, index) => (
                  <ConversationSkeleton
                    key={`conversation-skeleton-${index}`}
                  />
                ))}
              </Box>
            )}
            {conversationListContent}
          </ConversationList>

          {/* Banner "Voltar ao topo" */}
          {showBackToTop && (
            <Box
              sx={{
                position: "sticky",
                bottom: 0,
                left: "0",
                right: "0",
                height: "32px",
                backgroundColor: "rgba(0, 168, 132, 0.9)",
                backdropFilter: "blur(8px)",
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
                zIndex: 10,
                transition: "all 0.3s ease",
                cursor: "pointer",
                "&:hover": {
                  backgroundColor: "rgba(0, 143, 111, 0.95)",
                },
              }}
              onClick={scrollToTop}
            >
              <Box
                sx={{
                  display: "flex",
                  alignItems: "center",
                  gap: "6px",
                  color: "white",
                  fontSize: "12px",
                  fontWeight: "500",
                }}
              >
                <KeyboardArrowUp sx={{ fontSize: 16 }} />
                Voltar ao topo
              </Box>
            </Box>
          )}

          {/* Navegação entre páginas - FIXO na extremidade inferior */}
          {campaignsTotalPages > 0 && (
            <Box
              sx={{
                p: 2,
                textAlign: "center",
                borderTop: "1px solid #f0f0f0",
                backgroundColor: "#fff",
                position: "sticky",
                bottom: 0,
                zIndex: 11,
              }}
            >
              <Box
                sx={{
                  display: "flex",
                  gap: 1,
                  alignItems: "center",
                  justifyContent: "center",
                }}
              >
                <button
                  onClick={goToPreviousPage}
                  disabled={
                    campaignsLoading ||
                    campaignsPaginationLoading ||
                    campaignsPage <= 1
                  }
                  style={{
                    background:
                      campaignsPage <= 1 || campaignsPaginationLoading
                        ? "#f5f5f5"
                        : "#00a884",
                    color:
                      campaignsPage <= 1 || campaignsPaginationLoading
                        ? "#999"
                        : "white",
                    border:
                      campaignsPage <= 1 || campaignsPaginationLoading
                        ? "1px solid #e0e0e0"
                        : "none",
                    borderRadius: "6px",
                    padding: "8px 12px",
                    cursor:
                      campaignsPage <= 1 || campaignsPaginationLoading
                        ? "not-allowed"
                        : "pointer",
                    fontSize: "12px",
                    fontWeight: "500",
                    transition: "all 0.2s ease",
                    opacity:
                      campaignsPage <= 1 || campaignsPaginationLoading
                        ? 0.6
                        : 1,
                    display: "flex",
                    alignItems: "center",
                    gap: "4px",
                  }}
                  onMouseOver={(e) => {
                    if (
                      campaignsPage > 1 &&
                      !campaignsLoading &&
                      !campaignsPaginationLoading
                    ) {
                      e.currentTarget.style.backgroundColor = "#008f6f";
                    }
                  }}
                  onFocus={(e) => {
                    if (
                      campaignsPage > 1 &&
                      !campaignsLoading &&
                      !campaignsPaginationLoading
                    ) {
                      e.currentTarget.style.backgroundColor = "#008f6f";
                    }
                  }}
                  onMouseOut={(e) => {
                    if (
                      campaignsPage > 1 &&
                      !campaignsLoading &&
                      !campaignsPaginationLoading
                    ) {
                      e.currentTarget.style.backgroundColor = "#00a884";
                    }
                  }}
                  onBlur={(e) => {
                    if (
                      campaignsPage > 1 &&
                      !campaignsLoading &&
                      !campaignsPaginationLoading
                    ) {
                      e.currentTarget.style.backgroundColor = "#00a884";
                    }
                  }}
                >
                  {campaignsPaginationLoading && campaignsPage > 1 ? (
                    <>
                      <CircularProgress size={12} sx={{ color: "white" }} />
                      Carregando...
                    </>
                  ) : (
                    "← Anterior"
                  )}
                </button>

                <span
                  style={{ fontSize: "12px", color: "#666", padding: "0 8px" }}
                >
                  {campaignsPage} de {campaignsTotalPages}
                </span>

                <button
                  onClick={goToNextPage}
                  disabled={
                    campaignsLoading ||
                    campaignsPaginationLoading ||
                    campaignsPage >= campaignsTotalPages
                  }
                  style={{
                    background:
                      campaignsPage >= campaignsTotalPages ||
                      campaignsPaginationLoading
                        ? "#f5f5f5"
                        : "#00a884",
                    color:
                      campaignsPage >= campaignsTotalPages ||
                      campaignsPaginationLoading
                        ? "#999"
                        : "white",
                    border:
                      campaignsPage >= campaignsTotalPages ||
                      campaignsPaginationLoading
                        ? "1px solid #e0e0e0"
                        : "none",
                    borderRadius: "6px",
                    padding: "8px 12px",
                    cursor:
                      campaignsPage >= campaignsTotalPages ||
                      campaignsPaginationLoading
                        ? "not-allowed"
                        : "pointer",
                    fontSize: "12px",
                    fontWeight: "500",
                    transition: "all 0.2s ease",
                    opacity:
                      campaignsPage >= campaignsTotalPages ||
                      campaignsPaginationLoading
                        ? 0.6
                        : 1,
                    display: "flex",
                    alignItems: "center",
                    gap: "4px",
                  }}
                  onMouseOver={(e) => {
                    if (
                      campaignsPage < campaignsTotalPages &&
                      !campaignsLoading &&
                      !campaignsPaginationLoading
                    ) {
                      e.currentTarget.style.backgroundColor = "#008f6f";
                    }
                  }}
                  onFocus={(e) => {
                    if (
                      campaignsPage < campaignsTotalPages &&
                      !campaignsLoading &&
                      !campaignsPaginationLoading
                    ) {
                      e.currentTarget.style.backgroundColor = "#008f6f";
                    }
                  }}
                  onMouseOut={(e) => {
                    if (
                      campaignsPage < campaignsTotalPages &&
                      !campaignsLoading &&
                      !campaignsPaginationLoading
                    ) {
                      e.currentTarget.style.backgroundColor = "#00a884";
                    }
                  }}
                  onBlur={(e) => {
                    if (
                      campaignsPage < campaignsTotalPages &&
                      !campaignsLoading &&
                      !campaignsPaginationLoading
                    ) {
                      e.currentTarget.style.backgroundColor = "#00a884";
                    }
                  }}
                >
                  {campaignsPaginationLoading &&
                  campaignsPage < campaignsTotalPages ? (
                    <>
                      <CircularProgress size={12} sx={{ color: "white" }} />
                      Carregando...
                    </>
                  ) : (
                    "Próximo →"
                  )}
                </button>
              </Box>
            </Box>
          )}
        </ContentContainer>

        {isBulkMode && (
          <FloatingActionBar
            sx={{ bottom: campaignsTotalPages > 0 ? "80px" : "24px" }}
          >
            <Box
              sx={{
                display: "flex",
                flexDirection: "column",
                width: "100%",
                gap: 1,
              }}
            >
              <Box
                sx={{
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "space-between",
                  width: "100%",
                }}
              >
                <Typography
                  variant="body2"
                  sx={{ fontWeight: 600, color: "#008069" }}
                >
                  {selectedBulkChats.size} selecionado(s)
                </Typography>
                <Box sx={{ display: "flex", gap: 1 }}>
                  <Button
                    size="small"
                    onClick={handleSelectAll}
                    sx={{
                      color: "#00a884",
                      fontSize: "11px",
                      p: "2px 8px",
                      textTransform: "none",
                      fontWeight: 600,
                    }}
                  >
                    Todos
                  </Button>
                  <Button
                    size="small"
                    onClick={handleDeselectAll}
                    sx={{
                      color: "#667781",
                      fontSize: "11px",
                      p: "2px 8px",
                      textTransform: "none",
                      fontWeight: 600,
                    }}
                  >
                    Limpar
                  </Button>
                </Box>
              </Box>
              {isProcessingBulk ? (
                <Box
                  sx={{
                    display: "flex",
                    alignItems: "center",
                    width: "100%",
                    gap: 1.5,
                    py: 1,
                  }}
                >
                  <CircularProgress size={16} sx={{ color: "#00a884" }} />
                  <Box sx={{ flex: 1 }}>
                    <Box
                      sx={{
                        display: "flex",
                        justifyContent: "space-between",
                        mb: 0.5,
                      }}
                    >
                      <Typography
                        variant="caption"
                        sx={{ color: "#667781", fontWeight: 500 }}
                      >
                        Processando...
                      </Typography>
                      <Typography
                        variant="caption"
                        sx={{ color: "#00a884", fontWeight: 600 }}
                      >
                        {bulkProgress}%
                      </Typography>
                    </Box>
                    <Box
                      sx={{
                        height: "4px",
                        width: "100%",
                        bgcolor: "#f0f2f5",
                        borderRadius: "2px",
                        overflow: "hidden",
                      }}
                    >
                      <Box
                        sx={{
                          height: "100%",
                          width: `${bulkProgress}%`,
                          bgcolor: "#00a884",
                          transition: "width 0.2s ease",
                        }}
                      />
                    </Box>
                  </Box>
                </Box>
              ) : (
                <Box
                  sx={{
                    display: "flex",
                    gap: 0.5,
                    justifyContent: "space-between",
                    width: "100%",
                    mt: 0.5,
                  }}
                >
                  <Button
                    variant="contained"
                    size="small"
                    onClick={() => handleBulkAction("read")}
                    disabled={selectedBulkChats.size === 0}
                    sx={{
                      bgcolor: "#00a884",
                      "&:hover": { bgcolor: "#008f6f" },
                      fontSize: "11px",
                      textTransform: "none",
                      borderRadius: "8px",
                      py: 0.8,
                      flex: 1,
                      mr: 0.5,
                    }}
                  >
                    Lidas
                  </Button>
                  <Button
                    variant="outlined"
                    size="small"
                    onClick={() => handleBulkAction("enable_ai")}
                    disabled={selectedBulkChats.size === 0}
                    sx={{
                      color: "#00a884",
                      borderColor: "#00a884",
                      "&:hover": {
                        borderColor: "#008f6f",
                        bgcolor: "rgba(0, 168, 132, 0.04)",
                      },
                      fontSize: "11px",
                      textTransform: "none",
                      borderRadius: "8px",
                      py: 0.8,
                      flex: 1,
                      mx: 0.5,
                    }}
                  >
                    Ativar IA
                  </Button>
                  <Button
                    variant="outlined"
                    size="small"
                    onClick={() => handleBulkAction("disable_ai")}
                    disabled={selectedBulkChats.size === 0}
                    sx={{
                      color: "#d32f2f",
                      borderColor: "#d32f2f",
                      "&:hover": {
                        borderColor: "#c62828",
                        bgcolor: "rgba(211, 47, 47, 0.04)",
                      },
                      fontSize: "11px",
                      textTransform: "none",
                      borderRadius: "8px",
                      py: 0.8,
                      flex: 1,
                      ml: 0.5,
                    }}
                  >
                    Pausar IA
                  </Button>
                </Box>
              )}
            </Box>
          </FloatingActionBar>
        )}
      </Container>

      <Dialog
        open={metricsModalOpen}
        onClose={handleCloseMetrics}
        slotProps={{
          paper: {
            sx: {
              borderRadius: "12px",
              boxShadow: "0 8px 32px rgba(0, 0, 0, 0.12)",
              background: "linear-gradient(135deg, #ffffff 0%, #f8f9fa 100%)",
              border: "1px solid rgba(255, 255, 255, 0.8)",
              backdropFilter: "blur(10px)",
              width: "950px",
              maxHeight: "95vh",
              overflow: "hidden",
              "@media (max-width: 600px)": {
                width: "95vw",
                maxHeight: "95vh",
                margin: "8px",
                borderRadius: "8px",
              },
            },
          },
        }}
      >
        <DialogTitle
          sx={{
            p: 3,
            borderBottom: "1px solid #e9edef",
            background: "linear-gradient(135deg, #f8f9fa 0%, #ffffff 100%)",
            position: "relative",
          }}
        >
          <Box
            sx={{
              position: "absolute",
              top: 0,
              right: 0,
              width: 120,
              height: 120,
              background:
                "radial-gradient(circle, rgba(0, 168, 132, 0.05) 0%, transparent 70%)",
              borderRadius: "50%",
              transform: "translate(30px, -30px)",
            }}
          />
          <Box
            sx={{
              display: "flex",
              alignItems: "center",
              gap: 2,
              position: "relative",
              zIndex: 1,
            }}
          >
            <Box
              sx={{
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
                width: 42,
                height: 42,
                borderRadius: "10px",
                backgroundColor: "#e8f5e8",
                boxShadow: "0 2px 8px rgba(0, 0, 0, 0.08)",
                border: "1px solid rgba(255, 255, 255, 0.8)",
              }}
            >
              <Insights sx={{ color: "#00a884", fontSize: 22 }} />
            </Box>
            <Box sx={{ flex: 1 }}>
              <Typography
                variant="h6"
                sx={{
                  fontWeight: 700,
                  color: "#111b21",
                  mb: 0.5,
                  fontSize: "18px",
                }}
              >
                Métricas da campanha
              </Typography>
              <Typography
                variant="body2"
                sx={{
                  color: "#667781",
                  fontSize: "13px",
                  fontWeight: 500,
                }}
              >
                {metricsModalCampaign || "Campanha"}
              </Typography>
            </Box>
          </Box>
        </DialogTitle>
        <DialogContent sx={{ p: 0, overflow: "auto", maxHeight: "70vh" }}>
          <Box sx={{ pt: 1, px: 3, pb: 3 }}>
            <Box
              sx={{
                p: 2,
                backgroundColor: "#ffffff",
                borderRadius: "8px",
                border: "1px solid #00a884",
                boxShadow: "0 1px 6px rgba(0, 168, 132, 0.05)",
              }}
            >
              {metricsLoadingByCampaign.get(metricsModalCampaign) && (
                <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
                  <CircularProgress size={16} />
                  <Typography variant="body2" sx={{ color: "#667781" }}>
                    Carregando métricas de resposta...
                  </Typography>
                </Box>
              )}
              {!metricsLoadingByCampaign.get(metricsModalCampaign) && (
                <Box
                  sx={{
                    display: "grid",
                    gap: 3,
                  }}
                >
                  {/* Primeira linha: 2 métricas */}
                  <Box
                    sx={{
                      display: "grid",
                      gridTemplateColumns: { xs: "1fr", sm: "1fr 1fr" },
                      gap: 1.5,
                    }}
                  >
                    <Box
                      sx={{
                        textAlign: "center",
                        p: 1.5,
                        backgroundColor: "rgba(25, 118, 210, 0.08)",
                        borderRadius: "6px",
                        border: "1px solid rgba(25, 118, 210, 0.15)",
                      }}
                    >
                      <Typography
                        variant="subtitle1"
                        sx={{
                          color: "#1976d2",
                          fontWeight: 700,
                          fontSize: "16px",
                          mb: 0.25,
                        }}
                      >
                        {metricsByCampaign.get(metricsModalCampaign)
                          ?.responded ?? 0}
                      </Typography>
                      <Typography
                        variant="caption"
                        sx={{
                          color: "#1976d2",
                          fontWeight: 600,
                          textTransform: "uppercase",
                          letterSpacing: "0.3px",
                          fontSize: "9px",
                        }}
                      >
                        Respondidas
                      </Typography>
                    </Box>

                    <Box
                      sx={{
                        textAlign: "center",
                        p: 1.5,
                        backgroundColor: "rgba(158, 158, 158, 0.08)",
                        borderRadius: "6px",
                        border: "1px solid rgba(158, 158, 158, 0.2)",
                      }}
                    >
                      <Typography
                        variant="subtitle1"
                        sx={{
                          color: "#667781",
                          fontWeight: 700,
                          fontSize: "16px",
                          mb: 0.25,
                        }}
                      >
                        {metricsByCampaign.get(metricsModalCampaign)
                          ?.notResponded ?? 0}
                      </Typography>
                      <Typography
                        variant="caption"
                        sx={{
                          color: "#667781",
                          fontWeight: 600,
                          textTransform: "uppercase",
                          letterSpacing: "0.3px",
                          fontSize: "9px",
                        }}
                      >
                        Não Respondidas
                      </Typography>
                    </Box>
                  </Box>

                  {/* Segunda linha: 3 métricas */}
                  <Box
                    sx={{
                      display: "grid",
                      gridTemplateColumns: { xs: "1fr", sm: "repeat(3, 1fr)" },
                      gap: 1.5,
                    }}
                  >
                    <Box
                      sx={{
                        textAlign: "center",
                        p: 1.5,
                        backgroundColor: "rgba(46, 125, 50, 0.08)",
                        borderRadius: "6px",
                        border: "1px solid rgba(46, 125, 50, 0.15)",
                      }}
                    >
                      <Typography
                        variant="subtitle1"
                        sx={{
                          color: "#2e7d32",
                          fontWeight: 700,
                          fontSize: "16px",
                          mb: 0.25,
                        }}
                      >
                        {metricsByCampaign.get(metricsModalCampaign)
                          ?.positiveResponses ?? 0}
                      </Typography>
                      <Typography
                        variant="caption"
                        sx={{
                          color: "#2e7d32",
                          fontWeight: 600,
                          textTransform: "uppercase",
                          letterSpacing: "0.3px",
                          fontSize: "9px",
                        }}
                      >
                        Respostas Positivas
                      </Typography>
                    </Box>

                    <Box
                      sx={{
                        textAlign: "center",
                        p: 1.5,
                        backgroundColor: "rgba(245, 124, 0, 0.08)",
                        borderRadius: "6px",
                        border: "1px solid rgba(245, 124, 0, 0.18)",
                      }}
                    >
                      <Typography
                        variant="subtitle1"
                        sx={{
                          color: "#f57c00",
                          fontWeight: 700,
                          fontSize: "16px",
                          mb: 0.25,
                        }}
                      >
                        {metricsByCampaign.get(metricsModalCampaign)
                          ?.negativeResponses ?? 0}
                      </Typography>
                      <Typography
                        variant="caption"
                        sx={{
                          color: "#f57c00",
                          fontWeight: 600,
                          textTransform: "uppercase",
                          letterSpacing: "0.3px",
                          fontSize: "9px",
                        }}
                      >
                        Respostas Negativas
                      </Typography>
                    </Box>

                    <Box
                      sx={{
                        textAlign: "center",
                        p: 1.5,
                        backgroundColor: "rgba(198, 40, 40, 0.08)",
                        borderRadius: "6px",
                        border: "1px solid rgba(198, 40, 40, 0.18)",
                      }}
                    >
                      <Typography
                        variant="subtitle1"
                        sx={{
                          color: "#c62828",
                          fontWeight: 700,
                          fontSize: "16px",
                          mb: 0.25,
                        }}
                      >
                        {metricsByCampaign.get(metricsModalCampaign)
                          ?.blocklistResponses ?? 0}
                      </Typography>
                      <Typography
                        variant="caption"
                        sx={{
                          color: "#c62828",
                          fontWeight: 600,
                          textTransform: "uppercase",
                          letterSpacing: "0.3px",
                          fontSize: "9px",
                        }}
                      >
                        Blocklist
                      </Typography>
                    </Box>
                  </Box>
                </Box>
              )}

              {/* Gráficos de Rosca */}
              <Box sx={{ mt: 2 }}>
                <Box
                  sx={{
                    display: "grid",
                    gridTemplateColumns: { xs: "1fr", md: "1fr 1fr" },
                    gap: 3,
                    alignItems: "stretch",
                  }}
                >
                  {/* Gráfico 1: Respondidas vs Não Respondidas */}
                  <Box
                    sx={{
                      backgroundColor: "#ffffff",
                      borderRadius: "8px",
                      border: "1px solid #00a884",
                      boxShadow: "0 1px 6px rgba(0, 168, 132, 0.05)",
                      p: 2,
                      display: "flex",
                      flexDirection: "column",
                    }}
                  >
                    <Typography
                      variant="subtitle2"
                      sx={{
                        fontWeight: 600,
                        color: "#111b21",
                        textAlign: "center",
                        mb: 2,
                      }}
                    >
                      Status de Respostas
                    </Typography>
                    <Box
                      sx={{
                        height: "200px",
                        width: "100%",
                        position: "relative",
                      }}
                    >
                      <ResponsiveContainer width="100%" height="100%">
                        <PieChart>
                          <Pie
                            data={[
                              {
                                name: "Respondidas",
                                value:
                                  metricsByCampaign.get(metricsModalCampaign)
                                    ?.responded ?? 0,
                                color: "#1976d2",
                              },
                              {
                                name: "Não Respondidas",
                                value:
                                  metricsByCampaign.get(metricsModalCampaign)
                                    ?.notResponded ?? 0,
                                color: "#667781",
                              },
                            ].filter((item) => item.value > 0)}
                            cx="50%"
                            cy="50%"
                            innerRadius={40}
                            outerRadius={80}
                            paddingAngle={2}
                            dataKey="value"
                          >
                            {[
                              {
                                name: "Respondidas",
                                value:
                                  metricsByCampaign.get(metricsModalCampaign)
                                    ?.responded ?? 0,
                                color: "#1976d2",
                              },
                              {
                                name: "Não Respondidas",
                                value:
                                  metricsByCampaign.get(metricsModalCampaign)
                                    ?.notResponded ?? 0,
                                color: "#667781",
                              },
                            ]
                              .filter((item) => item.value > 0)
                              .map((item, index) => (
                                <Cell key={`cell-${index}`} fill={item.color} />
                              ))}
                          </Pie>
                          {(metricsByCampaign.get(metricsModalCampaign)
                            ?.responded ?? 0) +
                            (metricsByCampaign.get(metricsModalCampaign)
                              ?.notResponded ?? 0) >
                          0 ? (
                            <>
                              <RechartsTooltip
                                formatter={(
                                  value: number | undefined,
                                  name: string | undefined,
                                ) => {
                                  const total =
                                    (metricsByCampaign.get(metricsModalCampaign)
                                      ?.responded ?? 0) +
                                    (metricsByCampaign.get(metricsModalCampaign)
                                      ?.notResponded ?? 0);
                                  const numValue = value ?? 0;
                                  const percentage =
                                    total > 0
                                      ? ((numValue / total) * 100).toFixed(1)
                                      : "0.0";
                                  return [
                                    `${numValue} (${percentage}%)`,
                                    name ?? "",
                                  ];
                                }}
                              />
                              <Legend
                                verticalAlign="bottom"
                                height={36}
                                iconType="circle"
                              />
                            </>
                          ) : (
                            <Pie
                              data={[{ name: "Sem dados", value: 1 }]}
                              cx="50%"
                              cy="50%"
                              innerRadius={40}
                              outerRadius={80}
                              dataKey="value"
                              isAnimationActive={false}
                            >
                              <Cell fill="#f5f5f5" />
                            </Pie>
                          )}
                        </PieChart>
                      </ResponsiveContainer>
                      {(metricsByCampaign.get(metricsModalCampaign)
                        ?.responded ?? 0) === 0 &&
                        (metricsByCampaign.get(metricsModalCampaign)
                          ?.notResponded ?? 0) === 0 && (
                          <Box
                            sx={{
                              position: "absolute",
                              inset: 0,
                              display: "flex",
                              flexDirection: "column",
                              alignItems: "center",
                              justifyContent: "center",
                              textAlign: "center",
                              color: "#667781",
                              pointerEvents: "none",
                              px: 1,
                            }}
                          >
                            <Typography
                              variant="body2"
                              sx={{ fontWeight: 500 }}
                            >
                              Nenhuma resposta
                            </Typography>
                            <Typography variant="caption" sx={{ opacity: 0.7 }}>
                              ainda foi registrada
                            </Typography>
                          </Box>
                        )}
                    </Box>
                  </Box>

                  {/* Gráfico 2: Outras Métricas */}
                  <Box
                    sx={{
                      backgroundColor: "#ffffff",
                      borderRadius: "8px",
                      border: "1px solid #00a884",
                      boxShadow: "0 1px 6px rgba(0, 168, 132, 0.05)",
                      p: 2,
                      display: "flex",
                      flexDirection: "column",
                    }}
                  >
                    <Typography
                      variant="subtitle2"
                      sx={{
                        fontWeight: 600,
                        color: "#111b21",
                        textAlign: "center",
                        mb: 2,
                      }}
                    >
                      Tipo de Respostas
                    </Typography>
                    <Box
                      sx={{
                        height: "200px",
                        width: "100%",
                        position: "relative",
                      }}
                    >
                      <ResponsiveContainer width="100%" height="100%">
                        <PieChart>
                          <Pie
                            data={[
                              {
                                name: "Positivas",
                                value:
                                  metricsByCampaign.get(metricsModalCampaign)
                                    ?.positiveResponses ?? 0,
                                color: "#2e7d32",
                              },
                              {
                                name: "Negativas",
                                value:
                                  metricsByCampaign.get(metricsModalCampaign)
                                    ?.negativeResponses ?? 0,
                                color: "#f57c00",
                              },
                              {
                                name: "Blocklist",
                                value:
                                  metricsByCampaign.get(metricsModalCampaign)
                                    ?.blocklistResponses ?? 0,
                                color: "#c62828",
                              },
                            ].filter((item) => item.value > 0)}
                            cx="50%"
                            cy="50%"
                            innerRadius={40}
                            outerRadius={80}
                            paddingAngle={2}
                            dataKey="value"
                          >
                            {[
                              {
                                name: "Positivas",
                                value:
                                  metricsByCampaign.get(metricsModalCampaign)
                                    ?.positiveResponses ?? 0,
                                color: "#2e7d32",
                              },
                              {
                                name: "Negativas",
                                value:
                                  metricsByCampaign.get(metricsModalCampaign)
                                    ?.negativeResponses ?? 0,
                                color: "#f57c00",
                              },
                              {
                                name: "Blocklist",
                                value:
                                  metricsByCampaign.get(metricsModalCampaign)
                                    ?.blocklistResponses ?? 0,
                                color: "#c62828",
                              },
                            ]
                              .filter((item) => item.value > 0)
                              .map((item, index) => (
                                <Cell key={`cell-${index}`} fill={item.color} />
                              ))}
                          </Pie>
                          {(metricsByCampaign.get(metricsModalCampaign)
                            ?.positiveResponses ?? 0) +
                            (metricsByCampaign.get(metricsModalCampaign)
                              ?.negativeResponses ?? 0) +
                            (metricsByCampaign.get(metricsModalCampaign)
                              ?.blocklistResponses ?? 0) >
                          0 ? (
                            <>
                              <RechartsTooltip
                                formatter={(
                                  value: number | undefined,
                                  name: string | undefined,
                                ) => {
                                  const total =
                                    (metricsByCampaign.get(metricsModalCampaign)
                                      ?.positiveResponses ?? 0) +
                                    (metricsByCampaign.get(metricsModalCampaign)
                                      ?.negativeResponses ?? 0) +
                                    (metricsByCampaign.get(metricsModalCampaign)
                                      ?.blocklistResponses ?? 0);
                                  const numValue = value ?? 0;
                                  const percentage =
                                    total > 0
                                      ? ((numValue / total) * 100).toFixed(1)
                                      : "0.0";
                                  return [
                                    `${numValue} (${percentage}%)`,
                                    name ?? "",
                                  ];
                                }}
                              />
                              <Legend
                                verticalAlign="bottom"
                                height={36}
                                iconType="circle"
                              />
                            </>
                          ) : (
                            <Pie
                              data={[{ name: "Sem dados", value: 1 }]}
                              cx="50%"
                              cy="50%"
                              innerRadius={40}
                              outerRadius={80}
                              dataKey="value"
                              isAnimationActive={false}
                            >
                              <Cell fill="#f5f5f5" />
                            </Pie>
                          )}
                        </PieChart>
                      </ResponsiveContainer>
                      {(metricsByCampaign.get(metricsModalCampaign)
                        ?.positiveResponses ?? 0) === 0 &&
                        (metricsByCampaign.get(metricsModalCampaign)
                          ?.negativeResponses ?? 0) === 0 &&
                        (metricsByCampaign.get(metricsModalCampaign)
                          ?.blocklistResponses ?? 0) === 0 && (
                          <Box
                            sx={{
                              position: "absolute",
                              inset: 0,
                              display: "flex",
                              flexDirection: "column",
                              alignItems: "center",
                              justifyContent: "center",
                              textAlign: "center",
                              color: "#667781",
                              pointerEvents: "none",
                              px: 1,
                            }}
                          >
                            <Typography
                              variant="body2"
                              sx={{ fontWeight: 500 }}
                            >
                              Nenhum tipo de resposta
                            </Typography>
                            <Typography variant="caption" sx={{ opacity: 0.7 }}>
                              ainda foi registrado
                            </Typography>
                          </Box>
                        )}
                    </Box>
                  </Box>
                </Box>
              </Box>
            </Box>
          </Box>
        </DialogContent>
        <DialogActions sx={{ p: 2, borderTop: "1px solid #f0f2f5" }}>
          <Button
            onClick={handleDownloadCampaignMetrics}
            variant="contained"
            startIcon={<FileDownload sx={{ fontSize: 18 }} />}
            disabled={!metricsModalCampaign || metricsDownloadLoading}
            sx={{ borderRadius: "8px" }}
          >
            {metricsDownloadLoading ? "baixando..." : "download resumo"}
          </Button>
          <Button
            onClick={handleCloseMetrics}
            variant="outlined"
            sx={{ borderRadius: "8px" }}
          >
            Fechar
          </Button>
        </DialogActions>
      </Dialog>

      {/* Indicador de tempo real */}
    </>
  );
});

ChatSidebar.displayName = "ChatSidebar";

export default memo(ChatSidebar, () => true);
